import type { Dispatch, SetStateAction } from "react";
import { AxiosError, AxiosResponse } from "axios";
import type { LucideIcon } from "lucide-react";
import { DateRange } from "react-day-picker";
import { JSONContent } from "@tiptap/react";
import { AppRouter } from "@laborup/nova-client";
import type { inferRouterInputs, inferRouterOutputs } from "@trpc/server";

/* eslint-disable @typescript-eslint/consistent-type-definitions */

// Use Nova client's inferred types instead of manually maintained types
type RouterOutput = inferRouterOutputs<AppRouter>;
type RouterInput = inferRouterInputs<AppRouter>;

// Ensure id is required for these types since they're used throughout the codebase with required ids
// Both create and update return the same structure with required id
export type CertificationType =
  RouterOutput["internal"]["user"]["profile"]["certification"]["update"] & {
    id: number;
  };

export type EducationType =
  RouterOutput["internal"]["user"]["profile"]["education"]["update"] & {
    id: number;
  };

export type IndustryType =
  RouterOutput["internal"]["user"]["profile"]["industry"]["create"] & {
    id: number;
  };

export type SkillType =
  RouterOutput["internal"]["user"]["profile"]["skill"]["create"] & {
    id: number;
  };

export type WorkExperienceType =
  RouterOutput["internal"]["user"]["profile"]["workExperience"]["update"] & {
    id: number;
    internalCompany?: InternalCompany;
    // Cosine similarity (0-1) between this experience and the job being viewed.
    // Only present when the experience came back scoped to a job.
    relevanceCosineScore?: number | null;
  };

// MISC //

export interface AppearanceStore {
  isDarkMode: boolean;
  isWinterSidebar: boolean;
  setIsWinterSidebar: (isWinterSidebar: boolean) => void;
  toggleDarkMode: () => void;
}

// Page-break nudges injected between search result pages. Each shows at most
// once per iteration.
export enum SearchNudgeKind {
  DEEP_SCROLL = "DEEP_SCROLL",
  INTERESTING_EVERYONE = "INTERESTING_EVERYONE",
  NOTHING_LANDING = "NOTHING_LANDING",
  REVIEW_PILE = "REVIEW_PILE",
}

export type UserSheetFocusMode = {
  activeTab: string;
  // Kept as a plain string — each comms panel owns its own scope values and
  // falls back to its default when the saved one doesn't apply.
  communicationsScope: string | null;
  communicationsWidthPercent: number;
  isActivityRailCollapsed: boolean;
  isCommunicationsOpen: boolean;
  isResumesCollapsed: boolean;
  paneWidthsPercent: number[];
  // null when the staffer never adjusted zoom — the sheet falls back to its
  // split-aware default instead of pinning one.
  resumeZoom: number | null;
  splitTabValues: string[];
};

// Pending shortlist-takeover: which candidacy/owner, and the shortlist action to
// run once ownership is resolved. Shared by every shortlist entry point.
export type ShortlistTakeover = {
  candidacyId: string;
  ownerUserId: number;
  onShortlist: () => void;
};

// A selectable option for DropDownCell: value + label, an optional icon, and the
// styling applied when it's the chosen chip.
export interface ChipSelectOption<T extends string> {
  value: T;
  label: string;
  icon?: LucideIcon;
  selectedClassName: string;
  iconClassName?: string;
}

// Source of truth for registered species. The adoption wizard separately gates
// exclusive species through each user's unlocked pet ids.
export const STAFFER_PET_SPECIES = [
  "FOX",
  "DRAGON",
  "CAT",
  "BLUE_JAY",
  "CARDINAL",
  "COCKATIEL",
  "KINGFISHER",
  "OWL",
  "CROW",
  "WREN",
  "BALD_EAGLE",
  "DUNE_WORM",
  "BAT",
  "PHOENIX",
  "DIREWOLF",
  "PETER",
] as const;

export type StafferPetSpecies = (typeof STAFFER_PET_SPECIES)[number];

export type StafferPetUnlockId = StafferPetSpecies | "CUSTOM_PET_COMMISSION";

export type PetPaletteId =
  | "EMBER"
  | "MOSS"
  | "OCEAN"
  | "ORCHID"
  | "ROSE"
  | "GOLD"
  | "NEBULA"
  | "AURORA"
  | "BRONZE"
  | "SILVER"
  | "PHX_RED"
  | "RAINBOW"
  | "CENTRE_COURT"
  | "CHAMPIONSHIP_WHITE"
  | "CORBIN"
  | "NONE";

export type PetPaletteUnlockId = PetPaletteId | "CUSTOM_COLOR_COMMISSION";

export type PetTransaction = {
  id: string;
  amount: number;
  description: string;
  createdAt: string;
  eventId?: string;
  type: PetTransactionType;
};

export type PetTransactionType = "INCOME" | "PURCHASE" | "INCENTIVE_REDEMPTION";

export type StafferPetCelebration = "single" | "triple";

export type StafferPetRewardType =
  | "SEARCH_V2_SHORTLIST"
  | "SUBMIT"
  | "PLACEMENT"
  | "WEEKLY_INTEREST_GOAL"
  | "WEEKLY_SHORTLIST_GOAL";

export type StafferPetTopperOffset = {
  x: number;
  y: number;
  scale?: number;
  // Degrees, layered on top of the art's base rotation (accessories only).
  rotation?: number;
};

// Admin override for the playyard sky. A null field follows the live value.
export type PlayyardSkyPreview = {
  phase: PlayyardSkyPhase | null;
  condition: PetYardWeatherCondition | null;
  isWindy: boolean;
  isFreezing: boolean;
};

// Everything the playyard scene draws from, derived from clock + weather.
// `vars` are CSS custom properties applied to the scene root.
export type PlayyardSky = {
  phase: PlayyardSkyPhase;
  condition: PetYardWeatherCondition;
  vars: Record<string, string>;
  sun: { x: number; y: number; opacity: number };
  moon: { x: number; y: number; opacity: number };
  starOpacity: number;
  nightAmount: number;
  // 0 through the day, ramps to 1 across golden hour, 1 until dawn.
  eveningProgress: number;
  cloudCount: number;
  isWindy: boolean;
  isFreezing: boolean;
  isWet: boolean;
  hasFireflies: boolean;
};

export type Pet = {
  userId: number;
  updatedAt?: string;
  equipped: {
    petId: StafferPetSpecies;
    colorId: PetPaletteId;
    topperId: string | null;
    accessoryId?: string | null;
    accessoryId2?: string | null;
  };
  unlocked: {
    petIds: StafferPetUnlockId[];
    colorIds: PetPaletteUnlockId[];
    topperIds: string[];
    accessoryIds?: string[];
  };
  topperOffsets?: Record<string, StafferPetTopperOffset>;
  accessoryAdjustments?: Record<string, StafferPetTopperOffset>;
  hasRolled: boolean;
  shouldHide: boolean;
  // Server-derived: last month's #1 revenue earner wears the crown topper over
  // whatever they have equipped. Read-only on the client.
  isCrowned?: boolean;
  // Owner preference: wear the crown only on the leaderboard, keeping their
  // own topper everywhere else.
  isCrownLeaderboardOnly?: boolean;
  // Server-computed from the ledger; read-only on the client.
  coinBalance: number;
  totalCoinsEarned: number;
  // Token economy: floor(totalCoinsEarned / 550) lifetime, all server-enforced.
  incentiveTokensEarned: number;
  incentiveTokensRedeemed: number;
  incentiveTokensAvailable: number;
};

// What a pet sprite draws from. Views that only show pets (the leaderboard)
// read this slice so coin-balance changes from the pets poll don't re-render them.
export type PetAppearance = Pick<
  Pet,
  | "userId"
  | "equipped"
  | "topperOffsets"
  | "accessoryAdjustments"
  | "isCrowned"
  | "isCrownLeaderboardOnly"
>;

export type StafferPetPaletteId = PetPaletteId;

export type StafferPetAnimationFrame = "IDLE" | "BLINK";

export type StafferPetRarity =
  | "COMMON"
  | "UNCOMMON"
  | "RARE"
  | "EPIC"
  | "LEGENDARY";

export type StafferPetPalette = {
  label: string;
  rarity: StafferPetRarity;
  rarityRank: number;
  outline: string;
  shadow: string;
  body: string;
  highlight: string;
  accentDark: string;
  accent: string;
  accentLight: string;
  belly: string;
  eye: string;
  eyeHighlight: string;
};

type ActivePlacement = {
  id: number;
  candidacyId: string;
  status:
    | PlacementStatus.OFFERED
    | PlacementStatus.ACCEPTED
    | PlacementStatus.EMPLOYED;
  dateOffered: string | null;
  dateAccepted: string | null;
  dateStarted: string | null;
  job: {
    id: string;
    title: string;
    companyId: string;
    company: {
      id: string;
      name: string;
    };
  };
};

export type Activity =
  | InternalCommentActivity
  | {
      type: "candidacyOwnerChange";
      candidacyOwnerChange: CandidacyOwnerChange;
      createdAt: string;
    }
  | {
      type: "candidacySummaryChange";
      candidacySummaryChange: CandidacySummaryChange;
      createdAt: string;
    }
  | {
      type: "candidacyInterest";
      candidacyInterest: CandidacyInterest;
      createdAt: string;
    }
  | {
      type: "candidacyJobChange";
      candidacyJobChange: CandidacyJobChange;
      createdAt: string;
    }
  | {
      type: "candidacyAgenticScoreChange";
      candidacyAgenticScoreChange: CandidacyAgenticScoreChange;
      createdAt: string;
    }
  | {
      type: "userLaborupProRankChange";
      userLaborupProRankChange: UserLaborupProRankChange;
      createdAt: string;
    }
  | {
      type: "userPermanentStatusChange";
      userPermanentStatusChange: UserPermanentStatusChange;
      createdAt: string;
    };

export type CallSummary = {
  nextSteps: string[] | null;
  summary: string[] | null;
};

export type CallTranscriptLine = {
  content: string;
  role: "staffer" | "user";
  time: string;
};

export type CampaignPayload = {
  content: string;
  desiredPositionSelections: string[];
  shouldTargetNullDesiredPosition: boolean;
  shouldTargetUnregisteredUsers: boolean;
  locationAndRadiusSelections: { location: string; radius: number }[];
  campaignTagIncludeSelections: string[];
  campaignTagExcludeSelections: string[];
  phoneNumbersToExclude?: string;
  phoneNumbersToInclude?: string;
};

export type CandidacyEvaluation = {
  blocks: CandidacyEvaluationBlock[];
  createdAt: string;
  evaluationScore: number | null; // will correspond to the candidacy score unless candidacy score is updated elsewhere after the evaluation is submitted
  evaluationSummary: string | null;
  id: string;
  miscNotes: string | null;
  name: string;
};

export type CandidacyEvaluationBlock = {
  id: string;
  blockType: EvaluationBlockType;
  competency: string;
  description: string;
  scoringType?: EvaluationScoringType; // misc notes and summary do not have a scoring type attached to their block
  score: number | boolean | null;
  questions: CandidacyEvaluationQuestion[];
  notes: string | null;
};

type CandidacyEvaluationQuestion = {
  id: string;
  type?: "binary";
  required: boolean;
  question: string;
  answer?: boolean;
};

export type ShortcutEntry = {
  keys: string;
  description: string;
};

export type ShortcutSection = {
  id: SHORTCUT_SECTION_ID;
  label: string;
  shortcuts: ShortcutEntry[];
};

// Which part of a job page a teammate is looking at right now. Everything that
// isn't pipeline or search collapses into OTHER.
export enum JobPresenceTab {
  PIPELINE = "PIPELINE",
  SEARCH = "SEARCH",
  OTHER = "OTHER",
}

export type JobPresence = {
  user: User;
  tab: JobPresenceTab;
  // How long they've been observed on this job page, in ms.
  durationMs: number;
};

// One observed visit to a job page, accumulated locally from the all-users
// poll. The backend only stores where someone is right now, so recent history
// has to be remembered by each viewer.
export type JobVisit = {
  userId: number;
  jobId: string;
  tab: JobPresenceTab;
  // Epoch ms of the first heartbeat this viewer saw them here on — the clock
  // starts when observation starts, not when they actually arrived.
  firstSeenAtMs: number;
  // Epoch ms of the most recent heartbeat that put them on this page.
  lastSeenAtMs: number;
};

export enum JobPipelineSortKey {
  Name = "name",
  Staffer = "staffer",
  TimeInStage = "timeInStage",
  LastPing = "lastPing",
  Location = "location",
  Distance = "distance",
  AgenticScore = "agenticScore",
}

export enum AgenticScoreStatus {
  PENDING = "PENDING",
  COMPLETED = "COMPLETED",
  FAILED = "FAILED",
}

export enum AgenticScoreBucket {
  EXCELLENT = "EXCELLENT",
  GREAT = "GREAT",
  GOOD = "GOOD",
  OKAY = "OKAY",
}

export enum DashboardSchedulePanelView {
  CALENDAR = "CALENDAR",
  TASKS = "TASKS",
}

// Good/warning/bad grade for a staffer performance stat on the dashboard.
export enum PerformanceTier {
  GREEN = "GREEN",
  ORANGE = "ORANGE",
  RED = "RED",
}

// One day column of the expanded schedule week grid.
export type DashboardScheduleWeekDay = {
  key: string;
  date: Date;
  weekdayLabel: string;
  isToday: boolean;
};

// One day section of the mini schedule agenda list.
export type DashboardScheduleDaySection = {
  key: string;
  listLabel: string;
  interviews: DashboardInterview[];
};

// A single interview positioned within the expanded week grid. leftPct/widthPct
// place overlapping events side by side in equal-width columns (Google-style).
export type DashboardScheduleWeekEventLayout = {
  interview: DashboardInterview;
  topPx: number;
  heightPx: number;
  leftPct: number;
  widthPct: number;
};

export enum ReviewDecision {
  SHORTLIST = "SHORTLIST",
  // Shortlist without kicking off the AI scheduling agent — a human handles it.
  SHORTLIST_MANUAL = "SHORTLIST_MANUAL",
  REJECT = "REJECT",
}

export enum ReviewPortalToastKind {
  AGENT_HANDLING = "AGENT_HANDLING",
  REJECT = "REJECT",
}

// Which Stevie text a manual route would send, or that it can't send one.
// Keep in sync with StevieRouteMode in guac src/types/index.ts.
export enum StevieRouteMode {
  BLOCKED = "BLOCKED",
  NEW = "NEW",
  RENUDGE = "RENUDGE",
}

// Reason is staffer-facing copy, set only when the mode is BLOCKED.
export type StevieRouteEligibility = {
  mode: StevieRouteMode;
  reason: string | null;
};

// Where a notification link was clicked from, read off the URL's ?mixpanel-attr=.
// Keep in sync with NotificationAttribution in guac
// src/admin-api/admin-notifications/types/index.ts.
export enum NotificationAttribution {
  JOB_OWNERSHIP_CHANGE = "job-ownership-change",
  JOB_ACTIVATED = "job-activated",
  TOP_CANDIDATE_DM = "top-candidate-dm",
  TOP_CANDIDATE_PAST_DAY = "top-candidate-past-day",
  BATCH_SENT_FOLLOW_UP = "batch-sent-follow-up",
}

// Which pitch the "Call this candidate!" badge tooltip makes.
export enum CallNowBadgeVariant {
  TOP_PRIORITY = "TOP_PRIORITY",
  SMART_SOURCED = "SMART_SOURCED",
}

// Keys for the structured summary fields for Bridgetown Stevie
export enum CallStructuredSummaryFieldKey {
  // Quick logistics
  NUT_ALLERGY = "NUT_ALLERGY",
  ENGLISH_PROFICIENCY = "ENGLISH_PROFICIENCY",
  LONGEST_TENURE = "LONGEST_TENURE",
  EXPECTED_PAY = "EXPECTED_PAY",
  // Highlights
  BEST_FIT = "BEST_FIT",
  RELEVANT_EXPERIENCE_HIGHLIGHTS = "RELEVANT_EXPERIENCE_HIGHLIGHTS",
  WHY_THEYLL_LIKE_IT = "WHY_THEYLL_LIKE_IT",
  EVIDENCE_SHOW_UP_AND_STAY = "EVIDENCE_SHOW_UP_AND_STAY",
  RISKS = "RISKS",
  // Hands-on experience
  PRODUCTION_EQUIPMENT = "PRODUCTION_EQUIPMENT",
  INGREDIENT_HANDLING = "INGREDIENT_HANDLING",
  PACKAGING = "PACKAGING",
  MATERIAL_HANDLING = "MATERIAL_HANDLING",
  SANITATION = "SANITATION",
  GMP_SOP_EXPERIENCE = "GMP_SOP_EXPERIENCE",
  TROUBLESHOOTING = "TROUBLESHOOTING",
  PRODUCTION_DOCUMENTATION = "PRODUCTION_DOCUMENTATION",
  // Schedule fit
  PREFERRED_SHIFT = "PREFERRED_SHIFT",
  ELIGIBLE_SHIFTS = "ELIGIBLE_SHIFTS",
  CONFIRMED_SCHEDULE = "CONFIRMED_SCHEDULE",
  // Reliability
  ATTENDANCE = "ATTENDANCE",
  TRANSPORTATION = "TRANSPORTATION",
  COMMUTE_TIME = "COMMUTE_TIME",
  // Retention
  REASON_FOR_LEAVING_LAST_ROLE = "REASON_FOR_LEAVING_LAST_ROLE",
  INTENT = "INTENT",

  // Generic catalog (every job outside the Bridgetown pilot)
  // Quick logistics
  PAY = "PAY",
  COMMUTE = "COMMUTE",
  SHIFT = "SHIFT",
  DRUG_AND_BACKGROUND = "DRUG_AND_BACKGROUND",
  // Hands-on experience, informational rows
  TRADE_SKILLS = "TRADE_SKILLS",
  EQUIPMENT_TOOLS_SYSTEMS = "EQUIPMENT_TOOLS_SYSTEMS",
  MODELS_BRANDS = "MODELS_BRANDS",
  PROCESS_OWNERSHIP = "PROCESS_OWNERSHIP",
  COMPLEXITY_INDEPENDENCE = "COMPLEXITY_INDEPENDENCE",
  QUALITY_PRECISION = "QUALITY_PRECISION",
}

// KEEP IN SYNC with guac (src/global/openai/call-structured-summary-catalog.ts).
// Which field list a summary was generated against. Decides the labels, groups
// and order the tab renders.
export enum CallStructuredSummaryCatalog {
  BRIDGETOWN = "BRIDGETOWN",
  GENERIC = "GENERIC",
}

// KEEP IN SYNC with guac (src/global/openai/call-structured-summary-catalog.ts).
// UNKNOWN is the topic never coming up, which renders differently from NO.
// INFO is an informational row: the call produced something and nobody is
// grading it. Renders grey.
export enum CallStructuredSummaryStatus {
  YES = "YES",
  MAYBE = "MAYBE",
  NO = "NO",
  INFO = "INFO",
  UNKNOWN = "UNKNOWN",
}

// KEEP IN SYNC with guac (src/global/openai/call-structured-summary-catalog.ts).
export enum CallStructuredSummaryGroup {
  QUICK_LOGISTICS = "QUICK_LOGISTICS",
  HIGHLIGHT = "HIGHLIGHT",
  HANDS_ON_EXPERIENCE = "HANDS_ON_EXPERIENCE",
  SCHEDULE_FIT = "SCHEDULE_FIT",
  RELIABILITY = "RELIABILITY",
  RETENTION = "RETENTION",
}

// Where a Stevie call lands on the recommendation badge. Derived from the
// candidacy's APR, never asked of the model.
export enum StevieCallRecommendationTier {
  STRONG_SUBMIT = "STRONG_SUBMIT",
  SUBMIT = "SUBMIT",
  REVIEW = "REVIEW",
  DO_NOT_SUBMIT = "DO_NOT_SUBMIT",
}

export enum CandidateSheetVariant {
  // Mounted inside a host that already owns a dialog (the review portal): no
  // sheet frame, no job banner, no kebab, read-only comms.
  EMBEDDED = "EMBEDDED",
  // The full slide-over from the job pipeline.
  SHEET = "SHEET",
}

export enum PipelineSheetType {
  CANDIDATE_SHEET = "CANDIDATE_SHEET",
  MESSAGE_SHEET = "MESSAGE_SHEET",
}

export enum StafferAgentScheduledMessageChannel {
  EMAIL = "EMAIL",
  SMS = "SMS",
}

// The two templated agent chains. FOLLOW_UP runs before the candidate has
// replied; MID_CONV_NUDGE after they replied and then went quiet.
export enum StafferAgentScheduledMessageKind {
  FOLLOW_UP = "FOLLOW_UP",
  MID_CONV_NUDGE = "MID_CONV_NUDGE",
}

export type JobPipelineSortState = {
  key: JobPipelineSortKey;
  direction: SortOrder;
};

export type DashboardSelectedUser = number | "all";

// Router state passed when a dashboard cell deep-links into a job pipeline.
export type PipelineNavState = {
  onlyCommsToSend?: boolean;
  stafferView?: DashboardSelectedUser;
};

export type TeamGoal = {
  id: number;
  month: number;
  year: number;
  submitsGoal: number;
  interviewsGoal: number;
  offersGoal: number;
  placementsGoal: number;
  revenueGoal: number;
  numStaffers: number;
};

export type TeamGoalActuals = {
  submits: number;
  interviews: number;
  offers: number;
  placements: number;
  revenue: number;
};

export type UserActuals = TeamGoalActuals & {
  userId: number;
};

export type TeamGoalWithActuals = {
  goal: TeamGoal | null;
  actuals: TeamGoalActuals;
  weeklyActuals: TeamGoalActuals;
  actualsByUser: UserActuals[];
  weeklyActualsByUser: UserActuals[];
  activeStafferCount: number;
};

export type JobPipelineState = {
  appliedCount: number;
};

export enum TeamGoalField {
  SUBMITS = "SUBMITS",
  INTERVIEWS = "INTERVIEWS",
  OFFERS = "OFFERS",
  PLACEMENTS = "PLACEMENTS",
  REVENUE = "REVENUE",
}

// Goals are defined monthly; the week view divides the monthly target by 4.
export enum GoalPeriod {
  MONTH = "MONTH",
  WEEK = "WEEK",
}

// The three KPIs the dashboard goal panels select between, driving the
// comparison and trend cards beside them.
export enum DashboardComparisonMetric {
  TIME_TO_SCREEN = "TIME_TO_SCREEN",
  SUBMITS = "SUBMITS",
  REVENUE = "REVENUE",
}

// The three metrics the dashboard Staffer Stats card cycles through.
export enum StafferStatKind {
  TIME_TO_SCREEN = "TIME_TO_SCREEN",
  SEARCH_SUBMITS = "SEARCH_SUBMITS",
  SOURCING_SUBMITS = "SOURCING_SUBMITS",
}

// How a tier segment renders on the earnings progress bar, in bar order (left → right)
export enum EarningsTierSegmentState {
  COMPLETE = "COMPLETE",
  CURRENT = "CURRENT",
  UPCOMING = "UPCOMING",
}

// Mirrors guac's CommissionLedgerEntryType
export enum CommissionLedgerEntryType {
  PAID = "PAID",
  CLAWBACK = "CLAWBACK",
  ADJUSTMENT = "ADJUSTMENT",
}

// Net payment state of a tracker row, derived from its ledger entries
export enum EarningsPaymentStatus {
  PAID = "PAID",
  UNPAID = "UNPAID",
  CLAWBACK = "CLAWBACK",
}

export enum EarningsEmploymentStatus {
  NOT_STARTED = "NOT_STARTED",
  STARTED = "STARTED",
  CHURNED = "CHURNED",
}

export enum EarningsTrackerFilter {
  ALL = "ALL",
  PAID = "PAID",
  UNPAID = "UNPAID",
  PROBATIONARY = "PROBATIONARY",
  BACKFILL = "BACKFILL",
  CLAIMED = "CLAIMED",
}

export enum EarningsPeriod {
  ALL_YEAR = "ALL_YEAR",
  LAST_QUARTER = "LAST_QUARTER",
  THIS_QUARTER = "THIS_QUARTER",
  THIS_MONTH = "THIS_MONTH",
}

export enum EarningsTrackerSortKey {
  CANDIDATE = "CANDIDATE",
  CREDITED_DATE = "CREDITED_DATE",
  START_DATE = "START_DATE",
  RETENTION = "RETENTION",
  REVENUE = "REVENUE",
  COMMISSION = "COMMISSION",
}

export interface EarningsTrackerSortState {
  key: EarningsTrackerSortKey;
  direction: SortOrder;
}

export type DashboardJob = Pick<
  Job,
  | "id"
  | "title"
  | "overview"
  | "responsibilities"
  | "qualifications"
  | "benefits"
  | "city"
  | "state"
  | "employmentType"
  | "compensationType"
  | "compensationMin"
  | "compensationMax"
  | "estimatedRevenueAmount"
  | "keywords"
  | "gcpTalentId"
  | "companyId"
  | "liquidity"
  | "liquidityBreakdown"
> & {
  companyName: string;
  companyLogoUrl: string | null;
  companySmallLogoUrl?: string | null;
  category: JobCategory | null;
  ctdReqs: number | null;
  jobInternalConfigId: string | null;
  ownerUserIds: number[];
  // Users overseeing the job without owning its reqs or candidacies.
  managerUserIds: number[];
  // Users who pinned this job, for the All view's per-staffer pin display.
  pinnedUserIds: number[];
  candidacies: {
    id: string;
    status: CandidacyStatus;
    firstName: string | null;
    lastName: string | null;
    contactsAttempted: number | null;
    nextActionAt: string | null;
    stafferAgentActive: boolean;
    ownerUserIds: number[];
    // Set when a staffer routed this candidacy through Stevie.
    // The call end lands once the candidate takes the call.
    stevieNudgedAt: string | null;
    stevieCallEndedAt: string | null;
  }[];
  // Each owner's last-viewed pipeline snapshot; the deltas read the selected one.
  appliedSnapshotsByUser: {
    userId: number;
    appliedCount: number;
    commsToSendCount: number;
  }[];
  searchInterestTalentIds: number[];
  priority: JobPriority | null;
  difficulty: JobDifficulty | null;
  bottleneck: JobBottleneck | null;
  pulseDescription: string | null;
  pulses: { userId: number; description: string; createdAt: string }[];
  status: JobStatus;
  pausedAt: string | null;
  pauseReason: string | null;
  pauseType: JobPauseType | null;
};

export interface DashboardReviewState {
  candidacyIds: string[];
  companyId: string;
  companyName: string;
  jobId: string;
  jobTitle: string;
}

export interface ReviewPortalToast {
  id: string;
  candidacyId: string;
  candidateName: string;
  jobId: string;
  kind: ReviewPortalToastKind;
}

export type DashboardInterview = {
  id: number;
  dateTime: string;
  durationMinutes: number | null;
  status: InterviewStatus | null;
  timezone: Timezone | null;
  type: InterviewType;
  isStafferAgentScheduled: boolean;
  ownerUserId: number | null;
  candidacy: {
    id: string;
    firstName: string | null;
    lastName: string | null;
  } | null;
  job: {
    id: string;
    title: string;
  };
  company: {
    id: string;
    name: string;
  };
};

export type DashboardData = {
  jobs: DashboardJob[];
  monthToDateRevenue: number;
  weekToDateRevenue: number;
  teamGoal: TeamGoalWithActuals;
};

export type DashboardTask = {
  id: number;
  // Null for general user-created tasks (no automated check-in type).
  type: TaskType | null;
  title: string | null;
  dueDate: string | null;
  completedDate: string | null;
  linkedEntityId: number | null;
  companyId: string | null;
  jobId: string | null;
  candidacyId: string | null;
  linkedStartDate: string | null;
  assigneeUserIds: number[];
};

export type CommsToSendCandidacy = {
  candidacyId: string;
  companyId: string;
  jobId: string;
  candidateName: string;
  jobTitle: string;
  companyName: string;
  assigneeUserIds: number[];
  // Null for an untimed shortlist candidacy — owed a comm, but not late.
  nextActionAt: string | null;
  agenticScore: number | null;
};

export type CalendarAndTasksData = {
  interviews: DashboardInterview[];
  tasks: DashboardTask[];
  commsToSend: CommsToSendCandidacy[];
};

export type PortfolioHealthScoreTier =
  | "healthy"
  | "watch"
  | "at-risk"
  | "critical";

export type PortfolioHealth = Record<PortfolioHealthScoreTier, number>;

export type PortfolioTrendWindow = "week" | "month" | "quarter";

export type CompanyReportWindow = "month" | "quarter" | "year";

/**
 * Numbers only. All labels, colors, titles, and formatting live on the client
 * keyed by metric. `changePercent` is signed — sign gives the arrow direction,
 * magnitude is the displayed percentage. Headline values are derived from the
 * latest series point(s) on the client.
 */
export type PortfolioTrendsType = {
  placementVelocity: { points: number[]; changePercent: number };
  criticalAndAtRisk: {
    critical: number[];
    atRisk: number[];
    changePercent: number;
  };
  reqsAddedVsResolved: {
    added: number[];
    resolved: number[];
    changePercent: number;
  };
  bottlenecks: {
    customer: number[];
    screening: number[];
    sourcing: number[];
  };
};

export type CommuteInfo = {
  distanceMiles: number;
  durationSeconds: number;
} | null;

export type CompanyInformationPayload = {
  companyAbbreviation: string;
  logoCompanyFileId: number | null;
  smallLogoCompanyFileId?: number | null;
  address: string | null;
  description: string | null;
  naicsCodes: string[];
  customerIndustries: string[];
  manufacturingCapabilities: string[];
  equipment: string[];
  materials: string[];
  headcount: number | null;
  foundedYear: string | null;
  location: string | null;
  dunsNumber: string | null;
  cageCode: string | null;
  paymentTerms: PaymentTerms;
  websiteUrl: string | null;
};

export enum DateFilterOption {
  Today = "TODAY",
  Yesterday = "YESTERDAY",
  LastWeek = "LAST_WEEK",
  ThisWeek = "THIS_WEEK",
  LastMonth = "LAST_MONTH",
  ThisMonth = "THIS_MONTH",
  LastQuarter = "LAST_QUARTER",
  ThisQuarter = "THIS_QUARTER",
  ThisYear = "THIS_YEAR",
  AllTime = "ALL_TIME",
  Custom = "CUSTOM",
}

export enum DemoPosition {
  LAMINATOR = "laminator",
  MACHINIST = "machinist",
  MAINTENANCE = "maintenance",
  WELDER = "welder",
}

export enum DemoRegion {
  BIRMINGHAM_AL = "birmingham",
  BOWLING_GREEN_KY = "bowling-green",
  CHATTANOOGA_TN = "chattanooga",
  CINCINNATI_OH = "cincinnati",
  CLARKSVILLE_TN = "clarksville",
  CLEVELAND_OH = "cleveland",
  COLUMBUS_OH = "columbus",
  DAYTON_OH = "dayton",
  HUNTSVILLE_AL = "huntsville",
  KNOXVILLE_TN = "knoxville",
  LEXINGTON_KY = "lexington",
  LOS_ANGELES_CA = "los-angeles",
  LOUISVILLE_KY = "louisville",
  MOBILE_AL = "mobile",
  NASHVILLE_TN = "nashville",
}

export type EvaluationBuild = {
  id: string;
  name: string;
  createdAt: string;
  rank: number;
  blocks: EvaluationBuildBlock[];
};

type EvaluationBuildBlock = {
  id: string;
  blockType: EvaluationBlockType;
  competency: string;
  description: string;
  scoringType: EvaluationScoringType;
  questions: EvaluationBuildQuestion[];
};

type EvaluationBuildQuestion = {
  id: string;
  type?: EvaluationQuestionType;
  required: boolean;
  question: string;
};

export enum EmailDirection {
  INCOMING = "INCOMING",
  OUTGOING = "OUTGOING",
}

export enum EmailStatus {
  SENT = "SENT",
  FAILED = "FAILED",
}

export enum EvaluationBlockType {
  OPENENDED = "open-ended",
  STRUCTURED = "structured",
}

export enum EvaluationQuestionType {
  BINARY = "binary",
}

export enum EvaluationScoringType {
  RATING = "rating",
  BINARY = "binary",
  COMPLETION = "completion",
}

export type ExistingOrPendingUser =
  RouterOutput["internal"]["user"]["search"]["byNameOrPhone"][number];

// Duplicate-user data returned by nova when creating a user that already exists.
export type ExistingOrPendingUserWithDetails = ExistingOrPendingUser & {
  email?: string | null;
};

export type ResumeUpload = {
  fileBase64: string;
  fileName: string;
  title: string;
};

// Snapshot of the create-user form, held while the duplicate-user dialog is
// open so the candidacy can still be created for the existing user.
export type PreservedFormData = {
  createUserRequest: CreateUserRequest;
  resumeUpload?: ResumeUpload;
  screeningQuestionsAndAnswers: string;
  source: CandidacySource;
  status: CandidacyStatus;
};

export type GenericSignupVariantPayload = {
  type: "generic";
  header?: string;
  subHeader?: string;
  buttonText?: string;
};

export type HttpError<T> = AxiosError<T>;
export type HttpResponse<T> = AxiosResponse<T>;

export type InternalCommentActivity =
  | {
      type: "candidacyStatusChange";
      candidacyStatusChange: CandidacyStatusChange;
      createdAt: string;
    }
  | {
      type: "internalCandidacyComment";
      internalCandidacyComment: InternalCandidacyComment;
      createdAt: string;
    };

export enum InterviewDialogMode {
  INTERVIEW = "INTERVIEW",
  SCREEN = "SCREEN", // needed if somebody wants to add screen directly to interviews table, as we don't know if it's scheduled or completed
  SCREEN_SCHEDULED = "SCREEN_SCHEDULED",
  SCREENED = "SCREENED",
}

export enum InterviewDuration {
  MINUTES_15 = "15",
  MINUTES_30 = "30",
  MINUTES_45 = "45",
  MINUTES_60 = "60",
  MINUTES_75 = "75",
  MINUTES_90 = "90",
  MINUTES_120 = "120",
  MINUTES_150 = "150",
  MINUTES_180 = "180",
}

export enum InterviewRoundType {
  PHONE_SCREEN = "PHONE_SCREEN",
  VIDEO_INTERVIEW = "VIDEO_INTERVIEW",
  ONSITE = "ONSITE",
  APTITUDE_TEST = "APTITUDE_TEST",
  SKILLS_TEST = "SKILLS_TEST",
  PANEL_INTERVIEW = "PANEL_INTERVIEW",
  EXECUTIVE_INTERVIEW = "EXECUTIVE_INTERVIEW",
}

// Internal for Screens and Customer for customer interviews
export enum InterviewType {
  INTERNAL = "INTERNAL",
  CUSTOMER = "CUSTOMER",
}

export type JobIntakeAttachment = {
  id: number;
  name: string;
  url: string;
  mimetype: string;
};

export type JobIntakeGeneralInfo = {
  siteId?: string | null;
  roleTitle?: string | null;
  employmentType?: EmploymentType | null;
  quantityNeeded?: number | null;
  quantityMaximum?: number | null;
  yearsOfExperiencePreferred?: string | null;
  details?: string | null;
  compensationMinListed?: number | null;
  compensationMaxListed?: number | null;
  absoluteMaxCompensation?: number | null;
  shiftDifferentialMin?: number | null;
  shiftDifferentialMax?: number | null;
  benefits?: string | null;
  physicalWorkConditions?: string | null;
  shiftPreferred?: ShiftType[] | null;
  shiftFlexible?: ShiftType[] | null;
  openToRelocation?: boolean | null;
  relocationDetails?: string | null;
  relocationPackageMin?: number | null;
  relocationPackageMax?: number | null;
  backgroundCheckRequired?: boolean | null;
  backgroundAndDrugTestDetails?: string | null;
  drugTestRequired?: boolean | null;
  overtimeAvailable?: boolean | null;
  overtimePayMultiplier?: number | null;
  overtimeAvailability?: OvertimeAvailability | null;
  overtimeHoursPerWeek?: string | null;
  overtimeDetails?: string | null;
};

export type JobIntakeTalentRecruitingHistory = {
  openDuration?: string | null;
  fillTimeUsually?: string | null;
  areOtherStaffingFirmsWorking: boolean;
  willOtherStaffingFirmsWork: boolean;
  isInternalTeamRecruiting: boolean;
  struggles?: string | null;
  numberOfCandidatesSubmitted?: number | null;
  numberOfInterviews?: number | null;
  extraContext?: string | null;
  customersFrustrations?: string | null;
};

export type JobIntakeCulture = {
  teamDescription?: string | null;
  managerDetails?: string | null;
  successfulTraitsAndPersonalities?: string | null;
  failingBehaviorsAndPersonalities?: string | null;
};

export type IdealCandidateProfile = {
  id: string;
  personaTitle?: string | null;
  backgroundDetails?: string | null;
  experienceDetails?: string | null;
  personality?: string | null;
};

export type AntiIdealCandidateProfile = {
  id: string;
  title?: string | null;
  commonFailurePatterns?: string | null;
  backgroundAndExperienceRedFlags?: string | null;
  personalityRedFlags?: string | null;
  extraDetails?: string | null;
};

export type InterviewRound = {
  id: string;
  title?: string | null;
  type?: InterviewRoundType | null;
  conductedBy: number[];
  shieldedHiringManager?: string | null;
  details?: string | null;
  commonQuestions?: string | null;
  passingInterviewDetails?: string | null;
  failingInterviewDetails?: string | null;
};

export type JobIntakeDatesAndUrgency = {
  deadlineFirstBatch?: string | null;
  deadlineFirstPlacement?: string | null;
  deadlineJobFinished?: string | null;

  assumptions?: string | null;
  details?: string | null;
};

export type JobIntakeScreeningQuestions = {
  dealBreakers: string | null;
  contextBuilders: string | null;
};

export type JobIntake = {
  id: number;
  jobId: string;

  stakeholders: Stakeholder[];
  generalInfo: JobIntakeGeneralInfo;
  talentRecruitingHistory: JobIntakeTalentRecruitingHistory;
  culture: JobIntakeCulture;
  idealCandidateProfiles: IdealCandidateProfile[];
  antiIdealCandidateProfiles: AntiIdealCandidateProfile[];
  laborupRound: JobIntakeLaborupRound;
  interviewProcess: InterviewRound[];
  datesAndUrgency: JobIntakeDatesAndUrgency;
  screeningQuestions: JobIntakeScreeningQuestions;
  attachments: JobIntakeAttachment[];

  createdAt: string;
  updatedAt: string;
};

export type JobIntakeLaborupRound = {
  submissionProcess: string | null;
  additionalExpectations: string | null;
};

export enum JobPipelineCandidateSheetTab {
  Profile = "profile",
  Resume = "resume",
  Screening = "screening",
  StafferCalls = "staffer-call",
  VoiceAICall = "voice-ai-call",
  Notes = "notes",
  OtherCandidacies = "other-candidacies",
  Evaluations = "evaluations",
  Placements = "placements",
  Interviews = "interviews",
}

export enum JobIntakeTab {
  Stakeholders = "stakeholders",
  GeneralInfo = "general-info",
  TalentRecruitingHistory = "talent-recruiting-history",
  Culture = "culture",
  IdealCandidateProfiles = "ideal-candidate-profiles",
  AntiIdealCandidateProfiles = "anti-ideal-candidate-profiles",
  InterviewProcess = "interview-process",
  DatesAndUrgency = "dates-and-urgency",
  ScreeningQuestions = "screening-questions",
}

export enum CompanyIntakeTab {
  SalesTeam = "sales-team",
  Stakeholders = "stakeholders",
  GeneralInfo = "general-info",
  Sites = "sites",
  TalentRecruitingHistory = "talent-recruiting-history",
  Culture = "culture",
  Business = "business",
}

export enum JobsSortKeyAndOrder {
  CreatedAtDesc = "Date posted (recent first)",
  CreatedAtAsc = "Date posted (oldest first)",
  TitleAsc = "Job title (A-Z)",
  TitleDesc = "Job title (Z-A)",
}

export type JobSpecificRelocationPayload =
  | {
      relocationAvailable: true;
      relocationMin: number;
      relocationMax: number;
    }
  | {
      relocationAvailable: false;
      relocationMin?: never;
      relocationMax?: never;
    };

export type JobSpecificSignupVariantPayload = {
  type: "jobSpecific";
  jobId: string;
  talentId: string; // The Google Talent ID.
  desiredPosition: string;
  heroImageUrl: string;
  location: string;
  jobTitle: string;
  industry: string;
  compensationMin: number;
  compensationMax: number;
  compensationType: CompensationType;
  badges: SignupVariantBadge[];
  shifts: ShiftType[];
  theWork: string;
  whatWereLookingFor: string;
  whyThisShop: string;
  ctaLabel?: string;
  buttonText?: string;
  screeningQuestions: SignupVariantScreeningQuestion[];
} & JobSpecificRelocationPayload;

// * IMPORTANT: groupByOptions values should be aligned with the enum values in admin-jobs.service.ts in guac
export enum JobTrackerGroupBy {
  RequisitionOwner = "requisitionOwner",
  RequisitionStatus = "requisitionStatus",
}

// * IMPORTANT: sortOptions should only contain values in the JobTrackerSortOptions enum in guac's types/index.ts
export enum JobTrackerSort {
  CompanyName = "companyName",
  CreatedAt = "createdAt",
  DueDate = "dueDate",
  LastUpdate = "lastUpdate",
  Revenue = "revenue",
}

export enum LeaderboardDetailRecordType {
  CALL = "CALL",
  STATUS_CHANGE = "STATUS_CHANGE",
  PLACEMENT = "PLACEMENT",
  OFFER = "OFFER",
}

// Outcome of an extended offer, derived from the placement's status.
export enum OfferOutcome {
  UNDETERMINED = "UNDETERMINED",
  ACCEPTED = "ACCEPTED",
  DECLINED = "DECLINED",
  REVOKED = "REVOKED",
}

export type CallDetailRecord = {
  type: LeaderboardDetailRecordType.CALL;
  id: string;
  date: string;
  candidateName: string | null;
  jobTitle: string | null;
  companyName: string | null;
  candidacyId: string | null;
  jobId: string | null;
  companyId: string | null;
  talentId: number | null;
  phone: string | null;
  duration: number | null;
  direction: CallDirection;
  status: CallStatus;
  transcript: {
    transcriptLines: CallTranscriptLine[];
  } | null;
  isEstimatedCandidacy: boolean;
  candidacySource: CandidacySource | null;
  ownerUserId: number; // Staffer the record is attributed to on the leaderboard
};

export type PlacementDetailRecord = {
  type: LeaderboardDetailRecordType.PLACEMENT;
  id: number;
  date: string;
  candidateName: string | null;
  jobTitle: string | null;
  companyName: string | null;
  candidacyId: string;
  jobId: string;
  companyId: string | null;
  revenue: number | null;
  status: PlacementStatus;

  /* Fields to tell the detail behind placements that require backfills or are backfills */
  isBackfill: boolean; // Re-fill of a requisition whose previous hire churned inside the backfill guarantee
  isChurned: boolean; // The person left the job inside the backfill guarantee period
  countsInPeriod: boolean; // False for rows that don't count toward this period's totals; shown for context only
  dateAccepted: string | null; // When this placement's candidate accepted the offer
  dateCredited: string | null; // The requisition's original fill date — the date the placement counts on
  dateEmploymentEnded: string | null; // When the person stopped working the job
  backfilledCandidateName: string | null; // The churned person this placement backfilled
  candidacySource: CandidacySource | null;
  ownerUserId: number; // Staffer the record is attributed to on the leaderboard
};

export type StatusChangeDetailRecord = {
  type: LeaderboardDetailRecordType.STATUS_CHANGE;
  id: number;
  date: string;
  candidateName: string | null;
  jobTitle: string | null;
  companyName: string | null;
  candidacyId: string;
  jobId: string;
  companyId: string | null;
  interviewDateTime: string | null;
  interviewTimezone: Timezone | null;
  candidacySource: CandidacySource | null;
  ownerUserId: number; // Staffer the record is attributed to on the leaderboard
};

export type OfferDetailRecord = {
  type: LeaderboardDetailRecordType.OFFER;
  id: number;
  date: string;
  candidateName: string | null;
  jobTitle: string | null;
  companyName: string | null;
  candidacyId: string;
  jobId: string;
  companyId: string | null;
  revenue: number | null;
  outcome: OfferOutcome;
  status: PlacementStatus;
  candidacySource: CandidacySource | null;
  ownerUserId: number; // Staffer the record is attributed to on the leaderboard
};

export type LeaderboardDetailRecord =
  | CallDetailRecord
  | StatusChangeDetailRecord
  | PlacementDetailRecord
  | OfferDetailRecord;

export enum LeaderboardMetric {
  DIALS = "DIALS",
  SCREENS = "SCREENS",
  SUBMITTALS = "SUBMITTALS",
  INTERVIEWS = "INTERVIEWS",
  OFFERS = "OFFERS",
  PLACEMENTS = "PLACEMENTS",
  REVENUE = "REVENUE",
}

export type LeaderboardHouseMetric =
  | "revenue"
  | "placements"
  | "screens"
  | "submittals"
  | "interviews";

export enum LeaderboardSort {
  Messages = "messages",
  Dials = "dials",
  TotalCallDuration = "totalCallDuration",
  Screens = "screens",
  Submittals = "submittals",
  Interviews = "interviews",
  Placements = "placements",
  Revenue = "revenue",
}

// A Moderate-or-better job on a staffer's dashboard board. Mirrors guac's
// StafferLiquidJobDto.
export type LeaderboardLiquidJob = {
  jobId: string;
  companyId: string;
  title: string;
  companyName: string;
  liquidity: JobLiquidity;
};

export type LeaderboardUser = {
  userId: number;
  firstName: string;
  lastName: string;
  messages: number;
  dials: number;
  totalCallDuration: number;
  screens: number;
  submittals: number;
  internalSubmittals: number;
  interviews: number;
  offers: number;
  outstandingOffers: number;
  placements: number;
  internalPlacements: number;
  revenue: number;
  previousMonthRevenue: number;
  isPreviousMonthRevenueWinner: boolean;
  // Current board state, not the selected period. Most liquid first.
  liquidJobs: LeaderboardLiquidJob[];
};

export enum NotificationCategoryId {
  IMMEDIATE = "IMMEDIATE",
  DAILY_BATCHED = "DAILY_BATCHED",
  TEAM = "TEAM",
}

export type NotificationFilterState = {
  categoryIds: NotificationCategoryId[];
  inboxFilter: NotificationInboxFilter;
  searchQuery: string;
  // Empty means every type; set when a surface opens scoped to one type
  types: NotificationType[];
};

// Mirrors guac's payloads for each notification
export type NotificationExcellentCandidatePayload = {
  candidacyId: string;
  companyId: string;
  jobId: string;
  candidateName: string;
  customerName: string;
  reqTitle: string;
  // Absent on notifications created before the score shipped in the payload
  score?: number;
};

export type NotificationInternalRecommendationPayload = {
  candidacyId: string;
  companyId: string;
  jobId: string;
  candidateName: string;
  customerName: string;
  recommendedByName: string;
  reqTitle: string;
};

export type NotificationInternalCommentMentionPayload = {
  candidacyId: string;
  commentId: number;
  companyId: string;
  jobId: string;
  candidateName: string;
  commentContent: string;
  customerName: string;
  mentionerName: string;
  reqTitle: string;
};

export type NotificationThreadReplyPayload = {
  candidacyId: string;
  commentId: number;
  companyId: string;
  jobId: string;
  candidateName: string;
  commentContent: string;
  customerName: string;
  replierName: string;
  reqTitle: string;
};

export type NotificationJobCommentMentionPayload = {
  commentId: number;
  companyId: string;
  jobId: string;
  commentContent: string;
  customerName: string;
  mentionerName: string;
  reqTitle: string;
};

export type NotificationCompanyCommentMentionPayload = {
  commentId: number;
  companyId: string;
  commentContent: string;
  customerName: string;
  mentionerName: string;
};

export type NotificationExpiringCandidatesPayload = {
  candidacies: {
    candidacyId: string;
    companyId: string;
    jobId: string;
    candidateName: string;
    candidateRating: number | null;
    customerName: string;
    reqTitle: string;
  }[];
};

export type NotificationCommsToSendPayload = {
  candidacies: {
    candidacyId: string;
    companyId: string;
    jobId: string;
    candidateName: string;
    candidateRating: number | null;
    customerName: string;
    reqTitle: string;
    // ISO timestamp the comm became due; "days late" is derived from it. Null
    // for an untimed shortlist candidacy, which is owed a comm but isn't late.
    nextActionAt: string | null;
  }[];
};

export type NotificationInterviewsTodayPayload = {
  interviews: {
    candidacyId: string;
    companyId: string;
    jobId: string;
    candidateName: string;
    customerName: string;
    reqTitle: string;
    dateTime: string;
  }[];
};

export type NotificationPlacementCheckInsPayload = {
  checkIns: {
    candidacyId: string;
    companyId: string;
    jobId: string;
    candidateName: string;
    customerName: string;
    reqTitle: string;
    milestone: number;
    dueDate: string;
  }[];
};

export type NotificationMissingStartDatesPayload = {
  placements: {
    candidacyId: string;
    companyId: string;
    jobId: string;
    candidateName: string;
    customerName: string;
    reqTitle: string;
  }[];
};

export type NotificationLeaderboardDailyPayload = {
  periodLabel: string;
  intro: string;
  teamTotal: number;
  rankings: {
    userId: number;
    fullName: string;
    slackUserId: string | null;
    submitCount: number;
    note: string;
  }[];
};

export type NotificationLeaderboardWeeklyWinner = {
  userId: number;
  fullName: string;
  slackUserId: string | null;
};

// Each category is null when nobody qualified that week — those rows are skipped.
export type NotificationLeaderboardWeeklyPayload = {
  revenue: {
    winners: NotificationLeaderboardWeeklyWinner[];
    amount: number;
  } | null;
  placements: {
    winners: NotificationLeaderboardWeeklyWinner[];
    count: number;
  } | null;
  fastestTimeToFill: {
    winners: NotificationLeaderboardWeeklyWinner[];
    days: number;
  } | null;
};

export type NotificationSearchLeaderboardMonthlyRanking = {
  userId: number;
  fullName: string;
  slackUserId: string | null;
  rank: number;
  rankChangeSinceLastPost: number | null;
  shortlistsFromSearch: number;
  submitsFromSearch: number;
  placementsFromSearch: number;
  addressedCount: number;
  medianHours: number | null;
  fastestSeconds: number | null;
  stuckInApplied: number;
  shortlistOmitted: number;
};

export type NotificationSearchLeaderboardMonthlyPayload = {
  periodLabel: string;
  rankings: NotificationSearchLeaderboardMonthlyRanking[];
  note: string;
};

export enum PersonalRecordSubCategory {
  FASTEST_TIME_TO_FILL = "FASTEST_TIME_TO_FILL",
  MOST_SUBMISSIONS_DAY = "MOST_SUBMISSIONS_DAY",
  HIGHEST_REVENUE_WEEK = "HIGHEST_REVENUE_WEEK",
  BIGGEST_PLACEMENT = "BIGGEST_PLACEMENT",
}

export type PlacementCandidacyContext = {
  candidacyId: string;
  companyId: string;
  jobId: string;
  candidateName: string;
  customerName: string;
  reqTitle: string;
};

export type NotificationPersonalRecordPayload = {
  subCategory: PersonalRecordSubCategory;
  value: number;
  previousBest: number | null;

  // Only set for FASTEST_TIME_TO_FILL / BIGGEST_PLACEMENT
  candidacyContext?: PlacementCandidacyContext;
  previousCandidacyContext?: PlacementCandidacyContext;

  // HIGHEST_REVENUE_WEEK only: labels for current and previous best week
  periodLabel?: string;
  previousPeriodLabel?: string;
};

export enum MilestoneSubCategory {
  PLACEMENT_COUNT = "PLACEMENT_COUNT",
  REVENUE_WEEK = "REVENUE_WEEK",
}

export type NotificationMilestoneUnlockedPayload = {
  subCategory: MilestoneSubCategory;
  milestone: number;
};

export type NotificationPausedJobsReminderPayload = {
  jobs: {
    jobId: string;
    companyId: string;
    reqTitle: string;
    customerName: string;
    pauseReason: string | null;
    pauseType: JobPauseType;
    pausedDays: number;
  }[];
};

export type NotificationStaleCandidaciesPayload = {
  jobs: {
    jobId: string;
    companyId: string;
    reqTitle: string;
    customerName: string;
    candidacies: {
      candidacyId: string;
      candidateName: string;
      candidateRating: number | null;
      status: CandidacyStatus;
      // ISO timestamp the staleness clock started; "stale for" is derived
      stalledSince: string;
    }[];
  }[];
};

export type NotificationTeamWideSubmitPayload = {
  companyId: string;
  jobId: string;
  customerName: string;
  reqTitle: string;
  totalCount: number;
  // searchCount is how many of that person's submits came from Laborup Search,
  // which pay a bigger coin reward. Absent on notifications written before the
  // bonus was announced.
  submitters: {
    userId: number;
    firstName: string;
    count: number;
    searchCount?: number;
  }[];
};

export type NotificationBatchSentFollowUpPayload = {
  companyId: string;
  jobId: string;
  customerName: string;
  reqTitle: string;
  totalCount: number;
  // One entry per day candidates went out, oldest batch first. label is how the
  // day is spoken ("yesterday", "Thursday of last week").
  groups: {
    count: number;
    businessDaysSitting: number;
    label: string;
  }[];
  // The written #staffing-pod post, Slack mrkdwn and mentions included.
  message: string;
  // Whose names are on the batch, and the rest of the job's rotation, cc'd.
  ownerUserIds: number[];
  ccUserIds: number[];
};

export type NotificationInterviewBlocksFreedPayload = {
  // One entry per calendar block marked free, all on the same staffer.
  blocks: {
    candidacyId: string;
    interviewId: number;
    jobId: string;
    // The staffer's own calendar event title, so the block is recognizable.
    eventTitle: string;
    // ISO instant the interview, and so the freed block, starts.
    startAt: string;
  }[];
};

export type NotificationCandidateRequestedCallPayload = {
  candidacyId: string;
  companyId: string;
  jobId: string;
  talentId: number | null;
  // Latest agentic rating, null when the candidacy hasn't been rated yet.
  agenticScore: number | null;
  candidateName: string;
  customerName: string;
  // Stored for auditing a bad classification; deliberately not surfaced.
  messageContent: string;
  // How urgent the req is, so a high-priority job pulls harder than the rating.
  priority: JobPriority | null;
  // When the candidacy hit SHORTLIST. Null if it never passed through.
  shortlistedAt: string | null;
  // Both are needed to build the Quo conversation URL.
  quoConversationId: string | null;
  quoPhoneNumberId: string | null;
  reqTitle: string;
};

export type NotificationActiveCandidateFinishedSteviePayload = {
  candidacyId: string;
  companyId: string;
  jobId: string;
  voiceAIOutreachId: string;
  candidateName: string;
  customerName: string;
  reqTitle: string;
  // Null when the call produced the candidacy's first rating. toScore can sit
  // below it — a drop to great is still a screenable candidate.
  fromScore: number | null;
  toScore: number;
  status: CandidacyStatus;
};

export type NotificationCandidateResurrectedAfterSteviePayload = {
  candidacyId: string;
  companyId: string;
  jobId: string;
  voiceAIOutreachId: string;
  candidateName: string;
  customerName: string;
  reqTitle: string;
  // The rating before the call and what the call re-rated them to.
  // Taking the call is what resurrected them. The scores are context.
  fromScore: number | null;
  toScore: number;
};

export type NotificationCandidacyAutoScreenedPayload = {
  candidacyId: string;
  companyId: string;
  jobId: string;
  candidateName: string;
  customerName: string;
  reqTitle: string;
  // Where the candidacy sat when the auto-move fired.
  fromStatus: CandidacyStatus;
};

// The FE builds this notification's copy from the payload; the stored
// title/content are only a fallback.
export type NotificationIncentiveRedeemedPayload = {
  redeemerUserId: number;
  redeemerFirstName: string;
  // The redeemed incentive's title, e.g. "Date Night Gift Card".
  description: string;
};

export type NotificationJobPauseRequestDecidedPayload = {
  requestId: number;
  jobId: string;
  companyId: string;
  approved: boolean;
  companyName: string;
  deciderName: string;
  jobTitle: string;
  pauseType: JobPauseType;
};

// Types with no entry are team slack channel only and never reach the app
export type NotificationPayloadMap = {
  [NotificationType.EXCELLENT_CANDIDATE]: NotificationExcellentCandidatePayload;
  [NotificationType.INTERNAL_RECOMMENDATION]: NotificationInternalRecommendationPayload;
  [NotificationType.INTERNAL_COMMENT_MENTION]: NotificationInternalCommentMentionPayload;
  [NotificationType.THREAD_REPLY]: NotificationThreadReplyPayload;
  [NotificationType.JOB_COMMENT_MENTION]: NotificationJobCommentMentionPayload;
  [NotificationType.COMPANY_COMMENT_MENTION]: NotificationCompanyCommentMentionPayload;
  [NotificationType.EXPIRING_CANDIDATES]: NotificationExpiringCandidatesPayload;
  [NotificationType.INTERVIEWS_TODAY]: NotificationInterviewsTodayPayload;
  [NotificationType.PLACEMENT_CHECKINS]: NotificationPlacementCheckInsPayload;
  [NotificationType.MISSING_START_DATES]: NotificationMissingStartDatesPayload;
  [NotificationType.LEADERBOARD_DAILY]: NotificationLeaderboardDailyPayload;
  [NotificationType.LEADERBOARD_WEEKLY]: NotificationLeaderboardWeeklyPayload;
  [NotificationType.SEARCH_LEADERBOARD_MONTHLY]: NotificationSearchLeaderboardMonthlyPayload;
  [NotificationType.PERSONAL_RECORD]: NotificationPersonalRecordPayload;
  [NotificationType.MILESTONE_UNLOCKED]: NotificationMilestoneUnlockedPayload;
  [NotificationType.PAUSED_JOBS_REMINDER]: NotificationPausedJobsReminderPayload;
  [NotificationType.COMMS_TO_SEND]: NotificationCommsToSendPayload;
  [NotificationType.STALE_CANDIDACIES]: NotificationStaleCandidaciesPayload;
  [NotificationType.TEAM_WIDE_SUBMIT]: NotificationTeamWideSubmitPayload;
  [NotificationType.BATCH_SENT_FOLLOW_UP]: NotificationBatchSentFollowUpPayload;
  [NotificationType.INTERVIEW_BLOCKS_FREED]: NotificationInterviewBlocksFreedPayload;
  [NotificationType.CANDIDATE_REQUESTED_CALL]: NotificationCandidateRequestedCallPayload;
  [NotificationType.ACTIVE_CANDIDATE_FINISHED_STEVIE]: NotificationActiveCandidateFinishedSteviePayload;
  [NotificationType.CANDIDATE_RESURRECTED_AFTER_STEVIE]: NotificationCandidateResurrectedAfterSteviePayload;
  [NotificationType.CANDIDACY_AUTO_SCREENED]: NotificationCandidacyAutoScreenedPayload;
  [NotificationType.INCENTIVE_REDEEMED]: NotificationIncentiveRedeemedPayload;
  // Retired: pause requests are polled off job_pause_request and rendered in
  // the corner prompt stack, never stored as a notification. Mirrors guac,
  // where the enum value survives only because it is already in the database.
  [NotificationType.JOB_PAUSE_REQUEST]: never;
  [NotificationType.JOB_PAUSE_REQUEST_DECIDED]: NotificationJobPauseRequestDecidedPayload;
};

export type NotificationPayload =
  NotificationPayloadMap[keyof NotificationPayloadMap];

export enum NotificationChannel {
  IN_APP = "IN_APP",
  EMAIL = "EMAIL",
  SLACK = "SLACK",
}

// Mirrors guac's NOTIFICATION_CATALOG (admin-notifications/types)
// so FE notification settings are consistent with BE
// Types sharing a group render as one settings row and are written together
export enum NotificationPreferenceGroup {
  MENTIONS = "MENTIONS",
  TOP_CANDIDATES = "TOP_CANDIDATES",
}

export type NotificationCatalogEntry = {
  category: NotificationCategoryId;
  defaults: Record<NotificationChannel, boolean>;
  requiresAtLeastOneChannel: boolean;
  requiredChannel: NotificationChannel[];
  // Channels this type is never delivered on — locked off in settings
  unavailableChannel?: NotificationChannel[];
  preferenceGroup?: NotificationPreferenceGroup;
};

export enum NotificationInboxFilter {
  ALL = "ALL",
  UNREAD = "UNREAD",
}

// How loud a sidebar notification row is, borrowed from the agentic rating
// buckets so an excellent candidate waiting reads the same in both places.
export enum NotificationMenuTone {
  EXCELLENT = "EXCELLENT",
  GREAT = "GREAT",
  MENTION = "MENTION",
  DEFAULT = "DEFAULT",
}

export enum NotificationSelectionFilter {
  ALL = "ALL",
  READ = "READ",
  UNREAD = "UNREAD",
}

export enum NotificationType {
  EXCELLENT_CANDIDATE = "EXCELLENT_CANDIDATE",
  INTERNAL_RECOMMENDATION = "INTERNAL_RECOMMENDATION",
  INTERNAL_COMMENT_MENTION = "INTERNAL_COMMENT_MENTION",
  THREAD_REPLY = "THREAD_REPLY",
  JOB_COMMENT_MENTION = "JOB_COMMENT_MENTION",
  COMPANY_COMMENT_MENTION = "COMPANY_COMMENT_MENTION",
  EXPIRING_CANDIDATES = "EXPIRING_CANDIDATES",
  INTERVIEWS_TODAY = "INTERVIEWS_TODAY",
  PLACEMENT_CHECKINS = "PLACEMENT_CHECKINS",
  MISSING_START_DATES = "MISSING_START_DATES",
  LEADERBOARD_DAILY = "LEADERBOARD_DAILY",
  LEADERBOARD_WEEKLY = "LEADERBOARD_WEEKLY",
  SEARCH_LEADERBOARD_MONTHLY = "SEARCH_LEADERBOARD_MONTHLY",
  PERSONAL_RECORD = "PERSONAL_RECORD",
  MILESTONE_UNLOCKED = "MILESTONE_UNLOCKED",
  UNACTIONED_EXCELLENT_CANDIDATES = "UNACTIONED_EXCELLENT_CANDIDATES",
  UNACTIONED_EXCELLENT_CANDIDATES_PAST_DAY = "UNACTIONED_EXCELLENT_CANDIDATES_PAST_DAY",
  PAUSED_JOBS_REMINDER = "PAUSED_JOBS_REMINDER",
  COMMS_TO_SEND = "COMMS_TO_SEND",
  STALE_CANDIDACIES = "STALE_CANDIDACIES",
  TEAM_WIDE_SUBMIT = "TEAM_WIDE_SUBMIT",
  BATCH_SENT_FOLLOW_UP = "BATCH_SENT_FOLLOW_UP",
  INTERVIEW_BLOCKS_FREED = "INTERVIEW_BLOCKS_FREED",
  CANDIDATE_REQUESTED_CALL = "CANDIDATE_REQUESTED_CALL",
  ACTIVE_CANDIDATE_FINISHED_STEVIE = "ACTIVE_CANDIDATE_FINISHED_STEVIE",
  CANDIDATE_RESURRECTED_AFTER_STEVIE = "CANDIDATE_RESURRECTED_AFTER_STEVIE",
  CANDIDACY_AUTO_SCREENED = "CANDIDACY_AUTO_SCREENED",
  INCENTIVE_REDEEMED = "INCENTIVE_REDEEMED",
  JOB_PAUSE_REQUEST = "JOB_PAUSE_REQUEST",
  JOB_PAUSE_REQUEST_DECIDED = "JOB_PAUSE_REQUEST_DECIDED",
}

/** Vertical color stops for a leaderboard hexagon badge. */
export type HexGradient = {
  top: string;
  mid: string;
  bottom: string;
};

export type PipelineFilterAndSortState = {
  includeCouldNotContact: boolean;
  includeEmploymentEnded: boolean;
  includeRejected: boolean;
  locations: string[];
  name: string;
  sortKeyAndOrder: JobPipelineSortState | null;
  sources: CandidacySource[];
  excellentScore: boolean;
  greatScore: boolean;
  onlyCommsToSend: boolean;
  stevieCallCompleted: boolean;
  maxCommuteSeconds: number | null;
};

// Union type for table query params
export type QueryParamsType =
  | JobQueryParams
  | MyCandidaciesQueryParams
  | PipelineFilterAndSortState
  | PlacementQueryParams
  | TaskQueryParams;

export type QuoMessageMedia = { url: string; type: string }[];

export enum ResumeParserAction {
  OVERWRITE = "OVERWRITE",
  PARSE_AND_RECONCILE = "PARSE_AND_RECONCILE",
}

type ScreeningAnswer = {
  id: number;
  question: string;
  answer: string | null;
  required: boolean;
};

export enum ScreeningQuestionType {
  MultiSelect = "multi",
  TextField = "text",
}

export type ScreeningQuestion = {
  id: number;
  question: string;
  type?: ScreeningQuestionType;
  required: boolean;
  options?: string[];
};

export type SaveStatus = "idle" | "saving" | "saved";

export type SearchResult = {
  // TODO: Try to find a less hacky way to do this.
  allowFilter: boolean; // Prevent search results from being filtered on state changes such as viewed, not interested, and candidacy status until a new search is performed
  candidacyId: string | null;
  candidacyStatus: CandidacyStatus | null;
  certifications: CertificationType[];
  education: EducationType[];
  email: string;
  endorsementsReceived: Endorsement[];
  industries: IndustryType[];
  isHidden: boolean;
  isPending: boolean;
  laborupProRank: LaborupProRank | null;
  lastActiveAt: string | null;
  lastJobSeekingIntentAt: string;
  permanentStatus: string | null;
  placement: SearchResultPlacement | null;
  primaryResumeId: number | null;
  profile: SearchProfile;
  resumeFile: ResumeFileType | null;
  skills: SkillType[];
  summary: string;
  timeInStage: string;
  userId: number;
  viewed: boolean;
  workExperience: WorkExperienceType[];
};

export type SearchTerms = {
  booleanSearch: string;
  resumeLastUpdated: string;
  candidateLastActive: string;
  excludeContactedCandidates: string;
  timeAtCurrentJob: string;
};

export enum SignupVariantBadge {
  FAST_GROWING_COMPANY = "FAST_GROWING_COMPANY",
  FULL_MEDICAL_DENTAL_VISION = "FULL_MEDICAL_DENTAL_VISION",
  HIRING_MULTIPLE_ROLES = "HIRING_MULTIPLE_ROLES",
}

export type SignupVariantPayload =
  | GenericSignupVariantPayload
  | JobSpecificSignupVariantPayload;

export type SignupVariantScreeningQuestion = {
  id: number;
  question: string;
  required: boolean;
  type?: ScreeningQuestionType;
  options?: string[];
};

export enum SortOrder {
  Asc = "asc",
  Desc = "desc",
}

// How a candidacy entered the pipeline, for the Applied-stage source badge.
export enum SourcedType {
  INTERNAL = "INTERNAL",
  EXTERNAL = "EXTERNAL",
  RECOMMENDED = "RECOMMENDED",
  RESURRECTED = "RESURRECTED",
  STEVIE_NUDGED = "STEVIE_NUDGED",
}

export type DashboardSortKey =
  | "jobs"
  | "priority"
  | "applied"
  | "commsToSend"
  | "difficulty"
  | "bottleneck"
  | "staffer"
  | "submissions"
  | "finalists"
  | "ctd"
  | "ctdRev"
  | "revenue";

export interface DashboardSortState {
  key: DashboardSortKey;
  direction: SortOrder;
}

export type Stakeholder = {
  id: number;
  companyId: string;

  firstName: string | null;
  lastName: string | null;
  title: string | null;
  email: string | null;
  phone: string | null;
  linkedinUrl: string | null;
  role: StakeholderRole[] | null;
  personalityAndNotes: string | null;

  communicationMode: CommunicationMode[] | null;
  primaryContact: boolean;
  ccOnEmails: boolean;
  communicationStyle: string | null;
  communicationFrequency: CommunicationFrequency | null;
  communicationDetails: string | null;

  valuePerception: StakeholderValuePerception[] | null;
  valuedCandidateQualities: string | null;
  commonRejectionReasons: string | null;

  viewOnExternalPartners: string | null;

  createdAt: string;
  updatedAt: string;
};

export type StakeholderWithJobInfo = Stakeholder & {
  jobStakeholders: {
    job: {
      id: string;
      title: string;
    };
  }[];
};

export type StevieDemoConfig = {
  client: string;
  jobTitle: string;
  jobDescription: string;
  filterQuestionSections: {
    title: string;
    questions: string[];
  }[];
  openEndedQuestion: string;
};

export type TextAIOutreachWithProxy = {
  id: string;
  alternatePhoneNumber: string | null;
  candidacyId: string | null;
  status: TextAIOutreachStatus;
  proxyTextAIOutreachId: string | null;
  proxy: {
    candidacy: {
      id: Candidacy["id"];
    };
    company: {
      id: Company["id"];
      name: Company["name"];
    };
    job: {
      id: Job["id"];
      title: Job["title"];
    };
    textAIOutreach: {
      alternatePhoneNumber: string | null;
      candidacyId: string | null;
      status: TextAIOutreachStatus;
      proxyTextAIOutreachId: string | null;
    };
    voiceAIOutreachSlim: VoiceAIOutreachSlim | null;
  } | null;
};

export type TransformedCandidateDocument = {
  education: EducationType[]; // Education[] is returned when this is used to load candidacies for pipeline
  primaryResumeId: number | null;
  professionalSummary: string | null;
  profile: SearchProfile;
  workExperience: WorkExperienceType[]; // WorkExperience[] is returned when this is used to load candidacies for pipeline
  certifications: CertificationType[]; // Certification[] is returned when this is used to load candidacies for pipeline
  email: string;
  userId: number;
  resumeFile: ResumeFileType | null; // ResumeFile is returned when this is used to load candidacies for pipeline
  skills: SkillType[]; // Skill[] is returned when this is used to load candidacies for pipeline
  industries: IndustryType[]; // Industry[] is returned when this is used to load candidacies for pipeline
  placement: SearchResultPlacement | null;
  isPending: boolean;
  isOpenToRelocation: boolean;
  lastActiveAt: string | null;
  notes: string | null;
  laborupProRank: LaborupProRank | null;
  permanentStatus: string | null;
  lastJobSeekingIntentAt: string;
  linkedinUrl: string | null;
};

export type VoiceAIOutreachAdminMetadata = {
  completed?: boolean;
  completedBy?: string | null;
  completedAt?: string | null;
};

// TODO: Improve this!
export type VoiceAIOutreachPayload = {
  call: Record<string, unknown>;
  event: string;
};

// Verbatim transcript evidence attached to a summary section. Stored with the
// transcript's own roles. guac translates to nova's USER/INTERNAL on the wire.
export type VoiceAISummaryQuoteContextTurn = {
  speaker: "agent" | "candidate";
  text: string;
};

// A summary-derived citation as the candidacy-info endpoint returns it. The
// quoteContext speaks nova's citation vocabulary: USER is the candidate,
// INTERNAL is our side.
export type AiCallInsightCitation = {
  quote: string;
  quoteContext: { speaker: "INTERNAL" | "USER"; text: string }[];
  timestamp: string | null;
  voiceAIOutreachId: string;
  callAt: string;
};

export type VoiceAIOutreachAiSummaryJson = {
  motivation: {
    whyThisRole: string | null;
    followUp: string | null;
    timestamp: string | null;
    // Absent on summaries generated before quote capture shipped.
    quote?: string | null;
    quoteContext?: VoiceAISummaryQuoteContextTurn[] | null;
  };
  currentEmployment: {
    isCurrentlyEmployed: boolean | null;
    company: string | null;
    title: string | null;
    tenure: string | null;
    currentPay: string | null;
    reasonForChange: string | null;
    timestamp: string | null;
  };
  payAlignment: {
    desiredPay: string | null;
    isAlignedWithRange: boolean | null;
    notes: string | null;
    timestamp: string | null;
  };
  shiftAvailability: {
    isFlexible: boolean | null;
    preferredShifts: string | null;
    notes: string | null;
    timestamp: string | null;
  };
  locationAndCommute: {
    city: string | null;
    estimatedCommute: string | null;
    isCommuteAcceptable: boolean | null;
    notes: string | null;
    timestamp: string | null;
    // Absent on summaries generated before quote capture shipped.
    quote?: string | null;
    quoteContext?: VoiceAISummaryQuoteContextTurn[] | null;
  };
  drugAndBackgroundCheck: {
    willingToComplete: boolean | null;
    notes: string | null;
    timestamp: string | null;
  };
  workExperiences: {
    company: string | null;
    title: string | null;
    tenure: string | null;
    reasonForLeaving: string | null;
    timestamp: string | null;
  }[];
  dynamicQuestions: {
    question: string;
    answer: string | null;
    timestamp: string | null;
  }[];
  closingNotes: {
    candidateSentiment: string | null;
    redFlags: string | null;
    timestamp: string | null;
  };
  overallSummary: string | null;
};

export type VoiceAIOutreachScreeningSectionStatus =
  | "covered"
  | "partial"
  | "not_covered";

export type VoiceAIOutreachScreeningSummaryJson = {
  headline: string | null;
  logistics: {
    motivation: {
      summary: string | null;
      reasonForLeaving: string | null;
      jobSearchContext: string | null;
      timestamp: string | null;
    };
    pay: {
      desired: string | null;
      current: string | null;
      isBelievable: boolean | null;
      notes: string | null;
      timestamp: string | null;
    };
    commute: {
      location: string | null;
      estimate: string | null;
      isAcceptable: boolean | null;
      timestamp: string | null;
    };
    shift: {
      preferred: string | null;
      isFlexible: boolean | null;
      timestamp: string | null;
    };
    drugAndBackground: {
      willingToComplete: boolean | null;
      notes: string | null;
      timestamp: string | null;
    };
    status: VoiceAIOutreachScreeningSectionStatus;
  };
  experience: {
    relevantJobs: {
      company: string | null;
      title: string | null;
      years: string | null;
      reasonForLeaving: string | null;
      pay: string | null;
      description: string | null;
      relevanceScore: number | null;
      timestamp: string | null;
    }[];
    status: VoiceAIOutreachScreeningSectionStatus;
  };
  skills: {
    ratings: {
      skill: string;
      present: boolean | null;
      where: string | null;
      howLong: string | null;
      depthRating: number | null;
      notes: string | null;
      timestamp: string | null;
    }[];
    status: VoiceAIOutreachScreeningSectionStatus;
  };
  customerDealbreakers: {
    verdict: "pass" | "fail" | "unclear";
    items: {
      dealbreaker: string;
      status: "met" | "violated" | "unknown";
      evidence: string | null;
      timestamp: string | null;
    }[];
    status: VoiceAIOutreachScreeningSectionStatus;
  };
  overallSummary: string | null;
};

export type VoiceAIOutreachTranscript = {
  transcriptLines: {
    content: string;
    role: "agent" | "user";
    time: string;
  }[];
};

// Verbatim evidence behind one Stevie-tab field. `quoteContext` is the setup
// exchange only and never repeats the quote.
export type CallStructuredSummaryCitation = {
  quote: string;
  quoteContext: {
    speaker: "agent" | "candidate";
    text: string;
  }[];
  // "MM:SS" into the recording.
  timestamp: string | null;
};

export type CallStructuredSummaryField = {
  key: CallStructuredSummaryFieldKey;
  value: string | null;
  detail: string | null;
  // Populated for list fields only; empty for value fields.
  bullets: string[];
  status: CallStructuredSummaryStatus;
  citations: CallStructuredSummaryCitation[];
};

// Per-field breakdown of a Stevie call, generated after every completed call.
// Null on calls that predate it or whose generation failed.
export type CallStructuredSummary = {
  // Absent on summaries written before the generic catalog existed. Those are
  // all Bridgetown.
  catalog?: CallStructuredSummaryCatalog;
  fields: CallStructuredSummaryField[];
};

export type VoiceAIOutreachSlim = {
  id: VoiceAIOutreach["id"];
  scheduledDateTime: VoiceAIOutreach["scheduledDateTime"];
  status: VoiceAIOutreach["status"];
  adminMetadata: Pick<
    VoiceAIOutreachAdminMetadata,
    "completed" | "completedAt"
  > | null;
};

export type VoiceAiStaffingDynamicQuestion = {
  id: number;
  question: string;
};

// MODELS //

// The windows the Sadie dashboard can show. Each compares against the window
// of the same length just before it.
export enum SadieDashboardRange {
  LAST_7_DAYS = "LAST_7_DAYS",
  LAST_30_DAYS = "LAST_30_DAYS",
  LAST_90_DAYS = "LAST_90_DAYS",
}

// Whether a Sadie dashboard number reads as a count of workers or a rate.
export enum SadieStatFormat {
  COUNT = "COUNT",
  RATE = "RATE",
}

// One Sadie dashboard number for the window and the window before it. A rate
// carries the counts behind it too, since pilot-sized percentages swing on a
// handful of workers.
export interface SadieDashboardStat {
  value: number;
  previousValue: number | null;
  numerator?: number;
  denominator?: number;
}

// External applications Sadie ran for workers, each step counted by its own
// time in the window. Submitted means the ATS confirmed it.
export interface SadieDashboardExternalApplications {
  started: SadieDashboardStat;
  saidYes: SadieDashboardStat;
  submitted: SadieDashboardStat;
}

// Texts on Sadie's relationship threads in a window, both ways. Opted-out
// workers' texts count, since this measures volume.
export interface SadieDashboardTextMessages {
  total: SadieDashboardStat;
  fromWorkers: SadieDashboardStat;
  fromSadie: SadieDashboardStat;
}

// Referrals workers gave Sadie in a window, split by what became of them. A
// referral of someone Sadie already had is never recorded, so it isn't here.
export interface SadieDashboardReferrals {
  total: SadieDashboardStat;
  referrers: SadieDashboardStat;
  alreadyOnLaborup: SadieDashboardStat;
  newToLaborup: SadieDashboardStat;
  couldNotAdd: SadieDashboardStat;
  // Given a relationship before guac tracked which of the first two it was.
  unclassified: SadieDashboardStat;
}

// How a Sadie dashboard number moved since the window before. A rate's change
// is in percentage points, a count's in workers.
export interface SadieStatChange {
  amount: number;
  label: string;
}

export enum TrophyAwardType {
  BEST_TTFS = "BEST_TTFS",
  INTERNAL_PLACEMENTS = "INTERNAL_PLACEMENTS",
  INTERVIEWS = "INTERVIEWS",
  MOST_VERSATILE = "MOST_VERSATILE",
  PLACEMENTS = "PLACEMENTS",
  REVENUE = "REVENUE",
  SCREENS = "SCREENS",
  SUBMITTALS = "SUBMITTALS",
}

export enum TrophyValueUnit {
  DAYS = "DAYS",
  HOURS = "HOURS",
}

export type EarningsCommissionTier = {
  commissionPercent: number;
  thresholdDollars: number;
};

export type EarningsOffer = {
  id: number;
  candidacyId: string;
  candidateName: string;
  jobId: string;
  companyId: string;
  jobTitle: string;
  companyName: string;
  revenueEarned: number | null;
  lastCommunication: LastPing | null;
};

export type EarningsLedgerEntry = {
  id: number;
  type: CommissionLedgerEntryType;
  amount: number;
  effectiveDate: string;
  createdByUserId: number;
  reason: string | null;
  triggeredByCandidateName: string | null;
};

export type EarningsPlacement = {
  id: number;
  createdAt: string;
  status: PlacementStatus;
  candidacyId: string;
  candidateName: string;
  jobId: string;
  companyId: string;
  jobTitle: string;
  companyName: string;
  dateOffered: string;
  dateAccepted: string | null;
  creditedDate: string | null;
  dateSetToStart: string | null;
  dateStarted: string | null;
  dateEmploymentEnded: string | null;
  backfillGuaranteeDays: number;
  confirmedPastBackfillGuarantee: string | null;
  revenueEarned: number | null;
  commissionRate: number | null;
  isBackfill: boolean;
  isSuperseded: boolean;
  backfilledByUserId: number | null;
  ledgerEntries: EarningsLedgerEntry[];
};

export type EarningsJobInterview = {
  interviewId: number;
  candidacyId: string;
  candidateName: string;
  dateTime: string;
  timezone: Timezone | null;
  status: InterviewStatus | null;
};

export type EarningsJob = {
  jobId: string;
  companyId: string;
  jobTitle: string;
  companyName: string;
  estimatedRevenueAmount: number | null;
  earnableReqCount: number;
  protectReqCount: number;
  interviews: EarningsJobInterview[];
};

export type AtsJob = {
  id: string;
  atsJobId: string;
  laborupJobId: string | null;
  title: string | null;
  description: string | null;
  city: string | null;
  state: string | null;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
  url: string | null;
};

export type Call = {
  id: string;
  quoId: string;
  candidacyId: string | null;
  talentId: number | null;
  userId: number;
  direction: CallDirection;
  status: CallStatus;
  startedAt: string;
  duration: number | null;
  audioUrl: string | null;
  phone: string | null;
  evaluation: { justification: string; rating: number } | null;
  summary: CallSummary | null;
  transcript: { transcriptLines: CallTranscriptLine[] } | null;
  createdAt: string;
  updatedAt: string;
};

export type Campaign =
  RouterOutput["internal"]["admin"]["campaigns"]["findAll"][number]["campaign"];

export type CampaignTag =
  RouterOutput["internal"]["admin"]["campaigns"]["findAll"][number]["campaignTag"];

export type Candidacy = {
  id: string;
  jobId: string;

  aiSummary: string | null;
  aiSummaryShort: string | null;
  aiSummaryUpdatedAt: string | null;
  evaluations: CandidacyEvaluation[] | null;
  evaluationNew: CandidacyEvaluationNew | null;
  evaluationLastUpdatedAt: string | null;
  firstName: ExistingOrPendingUser["firstName"];
  lastActivityAt: string;
  lastName: ExistingOrPendingUser["lastName"];
  pendingTalentId: number | null;
  phone: ExistingOrPendingUser["phone"];
  talentId: number | null;
  screeningAnswers: { screeningAnswers: ScreeningAnswer[] } | null;
  stafferAgentActive: boolean;
  summary: string | null;
  execSummaryPacketUrl: string | null;
  execSummaryPacketGeneratedAt: string | null;
  source: CandidacySource | null;
  isExternallySourced: boolean;
  isAutoResurrected: boolean;
  quoConversationId: string | null;
  status: CandidacyStatus;

  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
};

export type CandidacyEvaluationFollowUp = {
  note: string;
  subtitle: string;
  title: string;
};

export type CandidacyEvaluationNew = {
  evaluation: string;
  followUps: CandidacyEvaluationFollowUp[];
};

export type CandidacyAgenticScoreChange = {
  id: number;
  candidacyId: string;
  userId: number | null; // userId means human staffer overrode agentic rating

  fromScore: number | null;
  toScore: number;
  justification: string | null;
  source: CandidacyAgenticScoreChangeSource;

  createdAt: string;
  updatedAt: string;
};

export type CandidacyInterest = {
  id: number;
  userId: number;
  candidacyId: string;
  isInterested: boolean;
  note: string;
  createdAt: string;
  updatedAt: string;
};

export type CandidacyJobChange = {
  id: number;
  candidacyId: string;
  fromJobId: string;
  toJobId: string;
  userId: number;
  createdAt: string;
  updatedAt: string;
};

export type CandidacyOwner = {
  id: number;
  candidacyId: string;
  userId: number;
  createdAt: string;
};

export type CandidacyOwnerChange = {
  id: number;
  candidacyId: string;
  userId: number;
  action: CandidacyOwnerChangeAction;
  performedByUserId: number;
  createdAt: string;
};

export type CandidacyPlacement = {
  id: number;
  // Set only on the update that moves the placement to EMPLOYED and earns the
  // owner a coin.
  stafferPetAward?: StafferPetAwardResponse;
  candidacyId: string;
  jobId: string;
  requisitionId: number;

  baseCompensation: number;
  candidacy: Candidacy;
  dateAccepted: string | null;
  dateEmploymentEnded: string | null;
  dateOffered: string;
  dateRejected: string | null;
  dateSetToStart: string | null;
  dateStarted: string | null; // Different from dateSetToStart to account for candidates that no-show their first day.
  retentionBonusDays: number | null;
  employmentEndedReason: string | null;
  ownerUserId: number;
  requisition: Requisition;
  // A placement can be made with a retention bonus, person churns, and backfill search does not use a retention bonus. That is why this field is needed on a placement. Same goes for retentionBonusDays
  retentionBonusAmount: number | null;
  revenueEarned: number | null;
  shiftDifferential: number | null;
  shiftType: ShiftType;
  shiftTypeDescription: string | null;
  status: PlacementStatus;

  createdAt: string;
  deletedAt: string | null;
  updatedAt: string;
};

export type CandidacyStatusChange = {
  id: number;
  candidacyId: string;
  userId: number;
  fromStatus: CandidacyStatus | null;
  toStatus: CandidacyStatus;
  statusChangeReason: string | null;
  byAgent: boolean;
  createdAt: string;
  updatedAt: string;
};

export type CandidacySummaryChange = {
  id: number;
  candidacyId: string;
  userId: number;
  toSummary: string;
  createdAt: string;
  updatedAt: string;
};

export type Company = {
  id: string;
  name: string;
  createdAt: string;
  updatedAt: string;
};

export type CompanyIntakeAttachment = {
  id: number;
  name: string;
  url: string;
  mimetype: string;
};

export type CompanyInternalConfig = {
  id: string;
  companyId: string;

  // Other company names that mean "affiliated with this customer": abbreviations,
  // legal names, sister companies.
  relatedCompanyNames: string[];

  createdAt: string;
  updatedAt: string;
};

export type CompanyIntake = {
  id: string;
  companyId: string;

  ownerId: number | null;
  stakeholders: Stakeholder[];
  general: CompanyIntakeGeneral;
  sales: CompanyIntakeSales;
  sites: CompanyIntakeSite[];
  talentRecruitingHistory: CompanyIntakeTalentRecruitingHistory;
  culture: CompanyIntakeCulture;
  business: CompanyIntakeBusiness;
  attachments?: CompanyIntakeAttachment[] | null;

  createdAt: string;
  updatedAt: string;
};

export type CompanyIntakeGeneral = {
  websiteUrl: string | null;
  industries: CompanyIndustry[] | null;
  headCount: string | null;
  whatTheyMake: string | null;
  rawMaterials: string | null;
  equipment: string | null;
  manufacturingProcess: string | null;
  maintenanceEnvironment: string | null;
  customers: string | null;
  standards: string | null;
  mediaLinks: { id: string; title: string | null; url: string | null }[];
};

export type CompanyIntakeSales = {
  details: string | null;
  dealTeam: number[];
  salesOwner: number | null;
};

export interface UpdateCompanyIntakeSalesRequest {
  details?: string;
  dealTeam?: number[];
  salesOwner?: number;
  salesTeam?: number[];
}

export interface CompanyIntakeSalesResponse {
  details?: string;
  dealTeam?: number[];
  salesOwner?: number;
  salesTeam?: number[];
}

export type CompanyIntakeSite = {
  id: string;
  siteName: string | null;
  address: string | null;
  headCount: string | null;
  city: string | null;
  state: string | null;
  zipCode: string | null;
  partnershipStartDate: string | null;
  notes: string | null;
  roleTypeBreakdown: {
    production: number | null;
    skilledTrades: number | null;
    professional: number | null;
  } | null;
  employmentTypeBreakdown: {
    temp: number | null;
    contract: number | null;
    fullTime: number | null;
  } | null;
};

export type CompanyIntakeTalentRecruitingHistoryGeneral = {
  retention: string | null;
  hiringChallenges: string | null;
  competitors: string | null;
  currentlyUsingStaffingFirms?: boolean | null;
  historicallyUsedStaffingFirms?: boolean | null;
  details: string | null;
};

export type CompanyIntakeTalentRecruitingHistoryInternal = {
  hrTeamSize: number | null;
  currentHrBandwidthOnRecruiting: number | null;
  idealHrBandwidthOnRecruiting: number | null;
  details: string | null;
};

export type CompanyIntakeTalentRecruitingHistoryExternal = {
  details: string | null;
  staffingFirmCostStructure: StaffingFirmCostStructure | null;
  staffingFirmCostDetails: string | null;
  likesAboutExternalRecruiters: string | null;
  dislikesAboutExternalRecruiters: string | null;
};

export type CompanyIntakeTalentRecruitingHistory = {
  general: CompanyIntakeTalentRecruitingHistoryGeneral;
  internal: CompanyIntakeTalentRecruitingHistoryInternal;
  external: CompanyIntakeTalentRecruitingHistoryExternal;
};

export type CompanyIntakeCulture = {
  companyCultureDetails: string | null;
  workplaceEnvironmentDetails: string | null;
};

export type CompanyIntakeBusiness = {
  growthStatus: BusinessTrend | null;
  headcountTrend: BusinessTrend | null;
  revenueAndHeadcountDetails: string | null;
  details: string | null;
};

export type CompanyComment = {
  id: number;
  content: JSONContent;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
  userId: number;
  companyId: string;
};

export type CompanyFile = {
  id: number;
  name: string;
  url: string;
  createdAt: string;
  updatedAt: string;
  companyId: string;
  userId: number;
  mimeType: string;
  user: {
    firstName: string;
    lastName: string;
  };
};

export type CompanyNote = {
  id: number;
  title: string;
  content: string;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
  userId: number;
  companyId: string;
  user: {
    firstName: string;
    lastName: string;
  };
};

export type CompanyInformation = {
  id: string;
  companyId: string;
  companyInformation: CompanyInformationPayload;
  createdAt: string;
  updatedAt: string;
};

export type CompanyUser = {
  firstName: string;
  lastName: string;
  email: string;
  isInvited: boolean;
  invitationStatus: UserInvitationStatus | null;
  inviterId: number | null;
};

export type DemandCompany = {
  name: string;
};

// TODO: This helps address a lot of the config is unknown issues but we should seek to improve this...
export type DemoConfig =
  RouterOutput["internal"]["admin"]["demoConfigs"]["findAll"][number] & {
    config: {
      autoFillJobUrl: string;
      region: DemoRegion;
      position: DemoPosition;
      stevie: StevieDemoConfig;
      hideCandidatesForCompanies: string[];
    };
  };

type EmploymentStatus = {
  title: string;
};

export type Email = {
  id: string;
  talentId: number | null;
  userId: number;

  bodyHtml: string;
  bodyText: string;
  direction: EmailDirection;
  gmailMessageId: string;
  gmailThreadId: string;
  rfcMessageId: string | null;
  sentAt: string;
  sentByAgent: boolean;
  status: EmailStatus;
  subject: string;

  createdAt: string;
  updatedAt: string;
};

export type Endorsement = {
  teamworkRating: number;
  technicalExperienceRating: number;
  leadershipRating: number;
  isVisible: boolean | null;
  desiredPosition: string | null;
  createdAt: string;
};

export type InternalCandidacyComment = {
  id: number;
  candidacyId: string;
  userId: number;
  threadId: number | null;
  content: string;
  contentJson: JSONContent; // Technically nullable, but we don't allow that.
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
};

export type ExternalCandidacyComment = {
  id: number;
  candidacyId: string;
  userId: number | null; // null when authored by an external user
  externalName: string | null; // set for external authors; render this when userId is null
  content: string;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
};

export type InternalCompany = {
  id: string;
  name: string;
  open?: boolean;
  verified?: boolean;
  isRelevant?: boolean;
  description?: string | null;
  logoUrl?: string | null;
  parentInternalCompanyId?: string | null;
  primaryInternalCompanyLocationId?: string | null;
  foundedYear?: number | null;
  websiteUrl?: string | null;
  linkedInUrl?: string | null;
  facebookUrl?: string | null;
  atsUrl?: string | null;
  internalCompanyPriorityId?: string | null;
  internalHeadcountRangeId?: string | null;
  notes?: string | null;
  createdAt?: string;
  updatedAt?: string;
};

export type InternalPhoneNumber =
  RouterOutput["internal"]["admin"]["campaigns"]["findAll"][number]["internalPhoneNumber"];

export type Job = {
  id: string;
  companyId: string;

  address: string;
  backfillGuaranteeDays: number | null;
  benefits: string | null;
  city: string;
  compensationMax: number;
  compensationMin: number;
  compensationType: CompensationType;
  description: string;
  employmentType: EmploymentType;
  estimatedRevenueAmount: number | null;
  gcpTalentId: string;
  isOpenToRelocation: boolean;
  keywords: JobKeyword[];
  overview: string;
  percentOfSalary: number | null;
  qualifications: string;
  positionCategory: PositionCategory | null;
  responsibilities: string;
  revenueType: RevenueType;
  screeningQuestions: { screeningQuestions: ScreeningQuestion[] } | null;
  shiftDifferentialMin: number | null;
  shiftDifferentialMax: number | null;
  shiftEnd: string | null;
  shiftStart: string | null;
  shiftType: ShiftType[];
  state: string;
  priority: JobPriority | null;
  status: JobStatus;
  pausedAt: string | null;
  pauseReason: string | null;
  pauseType: JobPauseType | null;
  // Only the job reads that feed the status dropdown carry these two; the
  // update and pause endpoints return the bare job row.
  pendingPauseRequest?: JobPendingPauseRequest | null;
  // Both must be true before guac will let the job go ACTIVE.
  activationReadiness?: {
    hasActiveRequisition: boolean;
    hasRotationOwner: boolean;
  };
  jobLinkGroupId: string | null;
  liquidity?: JobLiquidity | null;
  liquidityBreakdown?: JobLiquidityBreakdown | null;
  title: string;
  topCustomerPriority: string | null;
  zipCode: string;
  createdAt: string;
  updatedAt: string;
};

/** The one open pause/cover request on a job, if a staffer has filed one. */
export type JobPendingPauseRequest = {
  id: number;
  createdAt: string;
  pauseReason: string;
  pauseType: JobPauseType;
  requestedByName: string;
};

export type JobPauseRequest = {
  id: number;
  jobId: string;
  pauseReason: string;
  pauseType: JobPauseType;
  status: JobPauseRequestStatus;
  requestedByUserId: number;
  decidedByUserId: number | null;
  decidedAt: string | null;
  createdAt: string;
  updatedAt: string;
};

export type JobComment = {
  id: number;
  content: JSONContent;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
  userId: number;
  jobId: string;
};

export type JobFile = {
  id: number;
  name: string;
  url: string;
  createdAt: string;
  updatedAt: string;
  jobId: string;
  userId: number;
  mimeType: string;
};

// A job keyword, classified for chip color. Keywords generated from the job's
// qualifications; the ones flagged as default search keywords seed Search V2,
// the rest only highlight matches on candidate profiles.
export type JobKeyword = ClassifiedKeyword & {
  isDefaultSearchKeyword: boolean;
};

export type JobInternalConfig = {
  id: string;
  jobId: string;

  autoProfileRatingHumanInput: string | null;
  autoProfileRatingRubric: string | null;
  agenticStaffingPrompt: string | null;
  category: JobCategory | null;
  evaluations: EvaluationBuild[] | null;
  isAgentFlowEnabled: boolean;
  isWorkableJobPosted: boolean;
  isSponsoredOnIndeed: boolean;
  ownerId: number | null;
  stafferAgentActive: boolean;
  stafferCallEvaluationPrompt: string | null;
  stevieOutreachMaxRating: number;
  voiceAiStaffingDynamicQuestions: VoiceAiStaffingDynamicQuestion[] | null;

  createdAt: string;
  updatedAt: string;
};

export type JobNote = {
  id: number;
  title: string;
  content: string;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
  userId: number;
  jobId: string;
};

export type JobUpdate = {
  id: number;
  jobId: string;

  bottleneck: JobBottleneck | null;
  description: string | null;
  health: JobHealth | null;
  difficulty: JobDifficulty | null;
  urgency: JobUrgency | null;
  userId: number;

  createdAt: string;
  updatedAt: string;
};

export enum MediaLinkType {
  VIDEO_URL = "videoUrl",
  UPLOADED_IMAGE = "uploadedImage",
}

export type MediaLink = {
  id: string;
  type: MediaLinkType;
  title: string | null;
  url: string | null;
  companyFileId: number | null;
};

export type PendingUser = {
  id: number;
  employmentStatusId: number | null;
  userId: number | null;
  yoeRangeId: number | null;

  address: string | null;
  certifications: Record<string, unknown> | null;
  createdBy: string;
  currentPay: number | null;
  customMessage: string | null;
  desiredPay: number | null;
  desiredPosition: string | null;
  educations: Record<string, unknown> | null;
  email: string | null;
  firstName: string | null;
  flow: string | null;
  hash: string | null;
  industries: Record<string, unknown> | null;
  jobStatus: string | null;
  laborupProRank: LaborupProRank | null;
  lastName: string | null;
  location: string | null;
  notes: string | null;
  payload: Record<string, unknown> | null;
  permanentStatus: string | null;
  phone: string | null;
  professionalSummary: string | null;
  resumeFiles: Record<string, unknown> | null;
  skills: Record<string, unknown> | null;
  source: string | null;
  supportingDocuments: Record<string, unknown> | null;
  updatedBy: string | null;
  workExperiences: Record<string, unknown> | null;
  yoe: number | null;

  createdAt: string;
  updatedAt: string;
};

export type PendingUserLaborupProRankChange = UserLaborupProRankChange;

export type PendingUserPermanentStatusChange = UserPermanentStatusChange;

export type SearchInterest = {
  userId: number;
  jobId: string;
  talentId: number;
  isInterested: boolean;
  // Auto-flagged by the backend's top-candidate search seed.
  isTopCandidate: boolean;
  agenticScore: number | null;
  agenticScoreJustification: string | null;
  agenticScoreStatus: AgenticScoreStatus | null;
  aiSummaryShort: string | null;
  aiSummaryStatus: AgenticScoreStatus | null;
  commuteDurationSeconds: number | null;
  sessionId: string | null;
  deletedAt: string | null;
  createdAt: string;
  updatedAt: string;
  // Upsert response only: the per-10-interests milestone coin, and the weekly
  // interest goal bonus when this interest crossed it.
  stafferPetAward?: StafferPetAwardResponse;
  stafferPetGoalAward?: StafferPetAwardResponse;
};

// Lightweight placement type for search results
export type SearchResultPlacement = {
  candidacy: Pick<Candidacy, "talentId" | "pendingTalentId">;
  job: Pick<Job, "title"> & {
    company: Pick<Company, "name">;
  };
  createdAt: string;
};

export type PlacementStartSummary = {
  id: number;
  candidacyId: string;
  jobId: string;
  companyId: string;
  dateSetToStart: string | null;
  candidacy: {
    firstName: string | null;
    lastName: string | null;
  };
  ownerUser: {
    firstName: string;
    lastName: string;
  };
  job: {
    title: string;
  };
  company: {
    name: string;
  };
};

export type Interview = {
  id: number;
  jobId: string;
  candidacyId: string | null;
  title: string;
  description: string | null;
  dateTime: string;
  durationMinutes: number | null;
  status: InterviewStatus | null;
  timezone: Timezone | null;
  type: InterviewType;
  isStafferAgentScheduled: boolean;
  candidacy?: {
    firstName: string | null;
    lastName: string | null;
  } | null;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
  // Only present on an update response, when that update moved the interview's
  // Google Calendar event.
  calendarEventUpdated?: boolean;
};

export type InterviewSummary = {
  id: number;
  jobId: string;
  companyId: string;
  candidacyId: string | null;
  dateTime: string;
  durationMinutes: number | null;
  status: InterviewStatus | null;
  timezone: Timezone | null;
  title: string;
  description: string | null;
  candidacy: {
    firstName: string | null;
    lastName: string | null;
    latestOwner: { userId: number } | null;
  } | null;
  job: {
    title: string;
  };
  company: {
    name: string;
  };
};

export type CreateInterviewRequest = {
  candidacyId?: string | null;
  title: string;
  description?: string;
  dateTime: string;
  durationMinutes: number;
  status: InterviewStatus;
  timezone: Timezone;
  type?: InterviewType;
  isStafferAgentScheduled: boolean;
  createGoogleCalendarEvent?: boolean;
  createVideoConference?: boolean;
};

export type UpdateInterviewRequest = {
  candidacyId?: string | null;
  title?: string;
  description?: string | null;
  dateTime?: string;
  durationMinutes?: number;
  status?: InterviewStatus;
  timezone?: Timezone;
};

export type PlacementTrackerResponse = {
  id: number;
  candidacy: {
    id: string;
    name: string;
  };
  company: Company;
  dateAccepted: string | null;
  dateOffered: string;
  job: {
    city: string;
    id: string;
    pausedAt: string | null;
    pauseReason: string | null;
    pauseType: JobPauseType | null;
    state: string;
    status: JobStatus;
    title: string;
  };
  ownerName: string;
  revenueEarned: number | null;
  status: PlacementStatus;
};

export type Profile = {
  address: string | null;
  createdAt: string;
  currentPay: number | null;
  desiredPay: number | null;
  desiredPosition: string | null;
  employmentStatusId: number | null;
  firstName: string | null;
  id: number;
  jobStatus: string | null;
  lastName: string | null;
  latitude: number | null;
  location: string | null;
  longitude: number | null;
  phone: string;
  profileCompletionPct: number | null;
  referralCount: number | null;
  referralHash: string | null;
  source: string | null;
  updatedAt: string;
  userId: number;
  yoe: number | null;
  yoeRangeId: number | null;
};

export type QuoMessage = {
  id: string;
  talentId: number | null;
  userId: number;
  quoId: string;
  direction: QuoMessageDirection;
  body: string;
  media: QuoMessageMedia | null;
  status: QuoMessageStatus;
  sentAt: string;
  sentByAgent: boolean;
  createdAt: string;
  updatedAt: string;
};

export type Requisition = {
  id: number;
  externalReqId: string | null;
  jobId: string;

  dateDue: string | null;
  dateFilled: string | null;
  dateLost: string | null;
  dateProjectedClose: string | null;
  dateRequested: string | null;
  dateStarted: string | null;
  retentionBonusDays: number | null;
  lostReason: string | null;
  placements: CandidacyPlacement[];
  requisitionOwners: RequisitionOwner[];
  requisitionStatusChanges: RequisitionStatusChange[];
  retentionBonusAmount: number | null;
  shiftDifferentialMin: number | null;
  shiftDifferentialMax: number | null;
  shiftType: ShiftType[];
  shiftTypeDescription: string | null;
  status: RequisitionStatus;

  createdAt: string;
  deletedAt: string | null;
  updatedAt: string;
};

export type RequisitionOwner = {
  id: number;
  requisitionId: number;
  userId: number;

  createdAt: string;
};

export type RequisitionStatusChange = {
  id: number;
  requisitionId: number;
  userId: number;
  fromStatus: RequisitionStatus | null;
  toStatus: RequisitionStatus;
  createdAt: string;
  updatedAt: string;
};

export type ResumeFileType =
  RouterOutput["internal"]["user"]["active"]["generateCandidateArtifacts"]["resumeFile"] & {
    id: number;
    title: string;
  };

export type SearchProfile = {
  createdAt: string;
  currentPay: number | null;
  desiredPay: number | null;
  desiredPosition: string | null;
  firstName: string | null;
  employmentStatus: EmploymentStatus | null;
  hasMobileApp?: boolean;
  yoeRange: YearsOfExperienceRange | null;
  lastName: string | null;
  location: string | null;
  phone: string;
  // Special type that is added by the backend.
  hash: string | null;
};

export type SignupVariant =
  RouterOutput["internal"]["admin"]["dynamicPageVariants"]["findAll"][number];

export type SourcingRequest = {
  id: string;
  jobId: string;
  status: string;
  location: string;
  radiusMiles: number;
  searchTerms: SearchTerms;
  maxMessages: number | null;
  minimumScore: number | null;
  createdAt: string;
  updatedAt: string;
};

export type TextAIOutreach = {
  createdAt: string;
  id: string;
  candidacyId: string | null;
  metadata: Record<string, unknown> | null;
  status: TextAIOutreachStatus;
  proxyTextAIOutreachId: string | null;
  talentId: number | null;
  updatedAt: string;
  voiceAIOutreachId: string | null;
};

export type UserNote = {
  id: number;
  userId: number;
  ownerId: number;

  companyName: string | null;
  content: string;
  jobTitle: string | null;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
};

export type User = {
  id: number;
  companyId: string;

  email: string;
  firstName: string;
  lastName: string;
  quoUserId: string | null;
  quoPhoneNumberId: string | null;
  role: Role;
  userRole: UserRole | null;
  // Nested user_settings, pulled in one column at a time as callers need them.
  userSettings: {
    dashboardHeaderColor?: DashboardHeaderColor;
    receivesStafferEmployeeReports?: boolean;
  } | null;

  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
  pushApnsToken: string | null;
  lastActiveAt: string | null;
  lastPage: string | null;
};

export type UserLaborupProRankChange =
  RouterOutput["internal"]["user"]["status"]["active"]["proRank"]["listByUserId"][number];

export type UserPermanentStatusChange =
  RouterOutput["internal"]["user"]["status"]["active"]["permanent"]["listByUserId"][number];

export type View = {
  id: number;
  queryParams: QueryParamsType;
  name: string;
  tableName: ViewTableName;
  userId: number;
  jobId?: string;
  createdAt: string;
  deletedAt: string | null;
  updatedAt: string;
};

export type VoiceAIOutreach = {
  id: string;
  createdAt: string;
  updatedAt: string;
  candidacyId: string;
  scheduledDateTime: string;
  status: VoiceAIOutreachStatus;
  initiationType: VoiceAIOutreachInitiationType;
  transcript: VoiceAIOutreachTranscript | null;
  audioUrl: string | null;
  payload: VoiceAIOutreachPayload[] | null;
  adminMetadata: VoiceAIOutreachAdminMetadata | null;
  aiSummary: string | null;
  aiSummaryJson: VoiceAIOutreachAiSummaryJson | null;
  screeningSummaryJson: VoiceAIOutreachScreeningSummaryJson | null;
};

export type Task = {
  id: number;

  completedDate: string | null;
  createdById: number;
  dueDate: string | null;
  priority: TaskPriority;
  title: string | null;

  taskAssignees: TaskAssignee[];

  linkedEntityType: LinkedEntityType | null;
  linkedEntityId: number | null;

  createdAt: string;
  deletedAt: string | null;
  updatedAt: string;
};

export type TaskAssignee = {
  id: number;

  taskId: number;
  userId: number;

  createdAt: string;
};

type YearsOfExperienceRange = {
  range: string;
};

export enum BusinessTrend {
  GROWING = "GROWING",
  STABLE = "STABLE",
  DECLINING = "DECLINING",
}

export enum CallDirection {
  INCOMING = "INCOMING",
  OUTGOING = "OUTGOING",
}

export enum CallStatus {
  QUEUED = "QUEUED",
  INITIATED = "INITIATED",
  RINGING = "RINGING",
  IN_PROGRESS = "IN_PROGRESS",
  COMPLETED = "COMPLETED",
  BUSY = "BUSY",
  FAILED = "FAILED",
  NO_ANSWER = "NO_ANSWER",
  CANCELED = "CANCELED",
  MISSED = "MISSED",
  ANSWERED = "ANSWERED",
  FORWARDED = "FORWARDED",
  ABANDONED = "ABANDONED",
}

export enum CandidacyAgenticScoreChangeSource {
  COMPLETED_VOICE_AI_CALL = "COMPLETED_VOICE_AI_CALL",
  CREATED_AS_APPLIED = "CREATED_AS_APPLIED",
  MANUAL_TRIGGER_AFTER_APPLIED = "MANUAL_TRIGGER_AFTER_APPLIED",
  MANUAL_TRIGGER_AFTER_AI_CALL = "MANUAL_TRIGGER_AFTER_AI_CALL",
  MANUAL_HUMAN_OVERRIDE = "MANUAL_HUMAN_OVERRIDE",
}

export enum CandidacyOwnerChangeAction {
  ADDED = "ADDED",
  REMOVED = "REMOVED",
}

export enum PipelineStage {
  APPLIED = "APPLIED",
  IN_COMMUNICATION = "IN_COMMUNICATION",
  SCREEN_SCHEDULED = "SCREEN_SCHEDULED",
  TO_REVIEW = "TO_REVIEW",
  PREP_TO_SEND = "PREP_TO_SEND",
  SUBMITTED = "SUBMITTED",
  INTERVIEW = "INTERVIEW",
  PLACED = "PLACED",
}

export enum PipelineViewPreference {
  TABLE = "TABLE",
  KANBAN = "KANBAN",
}

export type DayOfWeek =
  | "Monday"
  | "Tuesday"
  | "Wednesday"
  | "Thursday"
  | "Friday"
  | "Saturday"
  | "Sunday";

// One contiguous time range within a day. "HH:MM" 24-hour.
export type AvailabilityTimeRange = {
  startTime: string;
  endTime: string;
};

// Weekly screening windows keyed by day. Empty array = unavailable that day.
export type ScreeningAvailability = Record<DayOfWeek, AvailabilityTimeRange[]>;

export enum DashboardHeaderColor {
  LABORUP_ORIGINAL = "LABORUP_ORIGINAL",
  NAVY = "NAVY",
  SLATE = "SLATE",
  FOREST = "FOREST",
  DUSTY_PINK = "DUSTY_PINK",
  SKY = "SKY",
  CORAL = "CORAL",
  MINT = "MINT",
  LILAC = "LILAC",
  PLUM = "PLUM",
  TEAL = "TEAL",
}

// Dashboard table columns a staffer can hide; user_settings stores the hidden set.
export enum DashboardHideableColumn {
  DIFFICULTY = "DIFFICULTY",
  BOTTLENECK = "BOTTLENECK",
  STAFFER = "STAFFER",
  CTD = "CTD",
  CTD_REV = "CTD_REV",
  COMMS_TO_SEND = "COMMS_TO_SEND",
  FINALISTS = "FINALISTS",
  LIQUIDITY = "LIQUIDITY",
  PRIORITY = "PRIORITY",
  APPS = "APPS",
  PULSE = "PULSE",
  SUBMISSIONS = "SUBMISSIONS",
}

export type UserSettings = {
  id: number;
  userId: number;

  dashboardHeaderColor: DashboardHeaderColor;
  dashboardHeaderMinimized: boolean;
  hiddenDashboardColumns: DashboardHideableColumn[];
  hiddenDashboardAllColumns: DashboardHideableColumn[];
  pipelineViewPreference: PipelineViewPreference;
  pipelineViewPreferenceUpdatedAt: string;
  pipelineFilterOnSelfDefault: boolean;
  screeningAvailability: ScreeningAvailability | null;
  showRacetrack: boolean;
};

// The current user's racetrack show/hide preference.
export type RacetrackData = {
  showRacetrack: boolean;
};

export type UpdateUserSettingsRequest = {
  pipelineViewPreference?: PipelineViewPreference;
  screeningAvailability?: ScreeningAvailability;
  showRacetrack?: boolean;
  dashboardHeaderColor?: DashboardHeaderColor;
  dashboardHeaderMinimized?: boolean;
  hiddenDashboardColumns?: DashboardHideableColumn[];
  hiddenDashboardAllColumns?: DashboardHideableColumn[];
};

export enum CandidacyStatus {
  APPLIED = "APPLIED",
  SHORTLIST = "SHORTLIST",
  OUTREACH = "OUTREACH",
  FOLLOWINGUP = "FOLLOWINGUP",
  ENGAGED = "ENGAGED",
  SCREENSCHEDULED = "SCREENSCHEDULED",
  SCREENED = "SCREENED",
  TOREVIEW = "TOREVIEW",
  ENRICHANDCHECK = "ENRICHANDCHECK",
  READYTOSEND = "READYTOSEND",
  BATCHSENT = "BATCHSENT",
  MATCHED = "MATCHED",
  INTERVIEW = "INTERVIEW",
  OFFER = "OFFER",
  PLACED = "PLACED",
  EMPLOYED = "EMPLOYED",
  EMPLOYMENT_ENDED = "EMPLOYMENT_ENDED",
  REJECTED = "REJECTED",
  CLOSED = "CLOSED",
  COULD_NOT_CONTACT = "COULD_NOT_CONTACT",
}

export enum CandidacyRejectionReason {
  SKILLS_GAP = "SKILLS_GAP",
  LOCATION_COMMUTE = "LOCATION_COMMUTE",
  PAY_MISALIGNMENT = "PAY_MISALIGNMENT",
  SCHEDULE_SHIFT_CONFLICT = "SCHEDULE_SHIFT_CONFLICT",
  TENURE = "TENURE",
  COULD_NOT_CONTACT = "COULD_NOT_CONTACT",
  WITHDREW_INTEREST = "WITHDREW_INTEREST",
  BACKGROUND_QUALIFICATIONS_CONCERN = "BACKGROUND_QUALIFICATIONS_CONCERN",
  SOFT_SKILLS_FIT = "SOFT_SKILLS_FIT",
  MOTIVATION = "MOTIVATION",
  FOUND_BETTER_FIT_CANDIDATE = "FOUND_BETTER_FIT_CANDIDATE",
  CUSTOMER_FEEDBACK_REDIRECTED = "CUSTOMER_FEEDBACK_REDIRECTED",
  CUSTOMER_REJECTED_CANDIDATE = "CUSTOMER_REJECTED_CANDIDATE",
  CANDIDATE_BECAME_UNRESPONSIVE = "CANDIDATE_BECAME_UNRESPONSIVE",
  CANDIDATE_WITHDREW_INTEREST = "CANDIDATE_WITHDREW_INTEREST",
  CANDIDATE_DECLINED_OFFER = "CANDIDATE_DECLINED_OFFER",
  BACKGROUND_DRUG_CHECK_FAILED = "BACKGROUND_DRUG_CHECK_FAILED",
  COMP_NEGOTIATION_FAILED = "COMP_NEGOTIATION_FAILED",
  ACCEPTED_COMPETING_OFFER = "ACCEPTED_COMPETING_OFFER",
  CUSTOMER_PULLED_OFFER = "CUSTOMER_PULLED_OFFER",
  OTHER = "OTHER",
}

export enum CandidacyRejectionReasonGroup {
  PROFILE_READ = "PROFILE_READ",
  COMMUNICATION_SCREEN = "COMMUNICATION_SCREEN",
  POST_SCREEN = "POST_SCREEN",
  SUBMITTED = "SUBMITTED",
  POST_OFFER = "POST_OFFER",
}

export enum CandidacySource {
  CCM = "CCM",
  HIGHMATCH = "HIGHMATCH",
  LABORUPSEARCH = "LABORUPSEARCH",
  LABORUPDIRECT = "LABORUPDIRECT",
  LABORUPCAMPAIGN = "LABORUPCAMPAIGN",
  INDEED = "INDEED",
  INTERNAL_SOURCING = "INTERNAL_SOURCING",
  INTERNAL_RECOMMENDATION = "INTERNAL_RECOMMENDATION",
  LINKEDIN = "LINKEDIN",
  CRAIGSLISTAD = "CRAIGSLISTAD",
  GOOGLESEARCHAD = "GOOGLESEARCHAD",
  METAAD = "METAAD",
  MONSTER = "MONSTER",
  SMARTSOURCING = "SMARTSOURCING",
  STAFFERDIRECT = "STAFFERDIRECT",
  STEVIEONBOARDING = "STEVIEONBOARDING",
  WORKABLE = "WORKABLE",
  ZIPRECRUITER = "ZIPRECRUITER",
  REFERRAL = "REFERRAL",
  OTHER = "OTHER",
  // A staffer unlocked this person's resume on an outside database. Distinct
  // from MONSTER / ZIPRECRUITER, which mean they applied through that board.
  JUICEBOX_RESUME_UNLOCK = "JUICEBOX_RESUME_UNLOCK",
  MONSTER_RESUME_UNLOCK = "MONSTER_RESUME_UNLOCK",
  ZIPRECRUITER_RESUME_UNLOCK = "ZIPRECRUITER_RESUME_UNLOCK",
}

export enum CommunicationFrequency {
  DAILY = "DAILY",
  FEW_WEEKLY = "FEW_WEEKLY",
  WEEKLY = "WEEKLY",
  MAJOR_UPDATES = "MAJOR_UPDATES",
  ONLY_ON_ESCALATION = "ONLY_ON_ESCALATION",
}

export enum CommunicationMode {
  CALL = "CALL",
  EMAIL = "EMAIL",
  TEXT = "TEXT",
  VIDEO = "VIDEO",
}

export enum CompanyIndustry {
  ADVANCED_MANUFACTURING = "ADVANCED_MANUFACTURING",
  AEROSPACE_AND_DEFENSE = "AEROSPACE_AND_DEFENSE",
  AUTOMOTIVE = "AUTOMOTIVE",
  CONSTRUCTION_MATERIALS = "CONSTRUCTION_MATERIALS",
  CONSUMER_PRODUCTION = "CONSUMER_PRODUCTION",
  HEAVY_EQUIPMENT_AND_MACHINERY = "HEAVY_EQUIPMENT_AND_MACHINERY",
  MATERIALS_PRODUCTION = "MATERIALS_PRODUCTION",
  METALS_AND_MACHINING = "METALS_AND_MACHINING",
  MARINE_MANUFACTURING = "MARINE_MANUFACTURING",
  OIL_AND_GAS = "OIL_AND_GAS",
  RENEWABLE_ENERGY = "RENEWABLE_ENERGY",
  WATER_AND_WASTE = "WATER_AND_WASTE",
}

export enum CompensationType {
  HOURLY = "HOURLY",
  YEARLY = "YEARLY",
}

// Whether the candidate expects to pass a drug screen. Verification status is
// deliberately not modelled here. A stated expectation is not a test result.
export enum DrugScreenStatus {
  WILL_NOT_PASS = "WILL_NOT_PASS",
  WILL_PASS = "WILL_PASS",
  WILL_PASS_EX_THC = "WILL_PASS_EX_THC",
}

export enum DuplicateField {
  PHONE = "PHONE",
  EMAIL = "EMAIL",
  BOTH = "BOTH",
  UNKNOWN = "UNKNOWN",
}

export enum EmploymentType {
  FULLTIME = "FULLTIME",
  PARTTIME = "PARTTIME",
  CONTRACT = "CONTRACT",
}

export enum OvertimeAvailability {
  REQUIRED = "REQUIRED",
  OPTIONAL = "OPTIONAL",
  SEASONAL = "SEASONAL",
  RARE = "RARE",
  UNLIMITED = "UNLIMITED",
}

export enum InterviewStatus {
  SCHEDULED = "SCHEDULED",
  CANDIDATE_CONFIRMED = "CANDIDATE_CONFIRMED",
  COMPLETED = "COMPLETED",
  CANDIDATE_NO_SHOW = "CANDIDATE_NO_SHOW",
  CANDIDATE_CANCELED = "CANDIDATE_CANCELED",
  CUSTOMER_CANCELED = "CUSTOMER_CANCELED",
}

export enum JobBottleneck {
  CUSTOMER = "CUSTOMER",
  SCREENING = "SCREENING",
  SOURCING = "SOURCING",
}

export enum JobCategory {
  PRODUCTION = "PRODUCTION",
  PROFESSIONAL = "PROFESSIONAL",
  SKILLED_TRADES = "SKILLED_TRADES",
}

export enum JobDifficulty {
  EASY = "EASY",
  MODERATE = "MODERATE",
  DIFFICULT = "DIFFICULT",
  VERY_DIFFICULT = "VERY_DIFFICULT",
}

export enum JobHealth {
  PENDING = "PENDING",
  ON_PACE = "ON_PACE",
  WARNING = "WARNING",
  AT_RISK = "AT_RISK",
  COMPLETED = "COMPLETED",
}

// The evidence behind a job's liquidity level (job-page tooltip): component
// scores plus the raw snapshot inputs they were computed from. Mirrors guac's
// JobLiquidityBreakdown.
export type JobLiquidityBreakdown = {
  level: JobLiquidity;
  underTested: boolean;
  marketName: string;
  positionCategory: PositionCategory;
  score: number | null;
  supplyScore: number | null;
  freshnessScore: number;
  proofScore: number;
  velocityScore: number;
  strongCount: number;
  active90StrongCount: number;
  placedTrailing12mo: number;
  medianTimeToFirstFillDays: number | null;
  workforceCount: number | null;
  computedAt: string;
};

export enum JobLiquidity {
  UNKNOWN = "UNKNOWN",
  THIN = "THIN",
  MODERATE = "MODERATE",
  LIQUID = "LIQUID",
  VERY_LIQUID = "VERY_LIQUID",
}

export enum JobPriority {
  HIGH = "HIGH",
  MEDIUM = "MEDIUM",
  LOW = "LOW",
  BACKBURNERED = "BACKBURNERED",
}

export enum JobStatus {
  ACTIVE = "ACTIVE",
  DRAFT = "DRAFT",
  CLOSED = "CLOSED",
}

// Which flavor of pause a job sits in. Only meaningful while pausedAt is set.
export enum JobPauseType {
  COVERED = "COVERED",
  PAUSED = "PAUSED",
}

// SUPERSEDED means the job was paused, covered or closed directly while the
// request was still pending, so there was nothing left to decide.
export enum JobPauseRequestStatus {
  PENDING = "PENDING",
  APPROVED = "APPROVED",
  DENIED = "DENIED",
  SUPERSEDED = "SUPERSEDED",
}

export enum JobUrgency {
  LOW = "LOW",
  MEDIUM = "MEDIUM",
  HIGH = "HIGH",
  URGENT = "URGENT",
}

export enum LaborupProRank {
  GOLD = "GOLD",
  SILVER = "SILVER",
  BRONZE = "BRONZE",
}

export enum LinkedEntityType {
  INTERVIEW = "INTERVIEW",
  PLACEMENT = "PLACEMENT",
}

export enum TaskType {
  PLACEMENT_DAY_BEFORE = "PLACEMENT_DAY_BEFORE",
  PLACEMENT_DAY_OF = "PLACEMENT_DAY_OF",
  PLACEMENT_THIRTY = "PLACEMENT_THIRTY",
  PLACEMENT_SIXTY = "PLACEMENT_SIXTY",
  PLACEMENT_NINETY = "PLACEMENT_NINETY",
  MISSING_START_DATE = "MISSING_START_DATE",
  INTERVIEW_DAY_BEFORE = "INTERVIEW_DAY_BEFORE",
  INTERVIEW_DAY_OF = "INTERVIEW_DAY_OF",
}

export enum DashboardScheduleTaskCategory {
  GENERAL = "GENERAL",
  CHECK_INS = "CHECK_INS",
  STARTS = "STARTS",
  INTERVIEWS = "INTERVIEWS",
  MISSING_START = "MISSING_START",
}

export enum PaymentTerms {
  NET15 = "NET15",
  NET30 = "NET30",
  NET60 = "NET60",
}

export enum PetYardWeatherCondition {
  CLEAR = "CLEAR",
  PARTLY_CLOUDY = "PARTLY_CLOUDY",
  OVERCAST = "OVERCAST",
  FOG = "FOG",
  DRIZZLE = "DRIZZLE",
  RAIN = "RAIN",
  SNOW = "SNOW",
  THUNDERSTORM = "THUNDERSTORM",
}

export enum PlayyardSkyPhase {
  DAWN = "DAWN",
  DAY = "DAY",
  GOLDEN_HOUR = "GOLDEN_HOUR",
  BLUE_HOUR = "BLUE_HOUR",
  NIGHT = "NIGHT",
}

export enum PlacementStatus {
  OFFERED = "OFFERED",
  ACCEPTED = "ACCEPTED",
  REJECTED = "REJECTED",
  REVOKED = "REVOKED",
  EMPLOYED = "EMPLOYED",
  COMPLETED = "COMPLETED",
}

export enum PositionCategory {
  PRODUCTION = "PRODUCTION",
  MACHINING = "MACHINING",
  WELDING = "WELDING",
  MAINTENANCE = "MAINTENANCE",
  ENGINEERING = "ENGINEERING",
}

// Mirrors guac's PushMode — how loud a mobile push is for the staffer
export enum PushMode {
  ALERT = "ALERT",
  QUIET = "QUIET",
  OFF = "OFF",
}

// Mirrors guac's PushType — each is one row in the mobile push settings
export enum PushType {
  EXCELLENT_CANDIDATE = "EXCELLENT_CANDIDATE",
  LEADERBOARD_TAKEOVER = "LEADERBOARD_TAKEOVER",
  STREAK_REMINDER = "STREAK_REMINDER",
  ASSISTANT_MESSAGE = "ASSISTANT_MESSAGE",
  APPLICATION = "APPLICATION",
}

export enum QuoMessageDirection {
  INCOMING = "INCOMING",
  OUTGOING = "OUTGOING",
}

export enum QuoMessageStatus {
  QUEUED = "QUEUED",
  SENT = "SENT",
  DELIVERED = "DELIVERED",
  UNDELIVERED = "UNDELIVERED",
}

export enum Role {
  EMPLOYER = "EMPLOYER",
  STAFFER = "STAFFER",
}

export enum RequisitionStatus {
  PENDING = "PENDING",
  BACKFILL = "BACKFILL",
  FILLED = "FILLED",
  LIVE = "LIVE",
  LOST = "LOST",
  WASH = "WASH",
  COMPLETED = "COMPLETED",
}

export enum RevenueType {
  FIXED = "FIXED",
  PERCENT = "PERCENT",
}

/**
 * Where a candidate ended up with Sally on one job. Mirrors guac's
 * SallyState (global/utils/sally-state.ts). Keep the two in sync!
 *
 * Grouped by what a staffer would do about it:
 *   working — PENDING_OUTREACH, IN_CONVERSATION (Sally still has it)
 *   won     — CONVERTED
 *   owed    — RULE_BLOCKED, UNREACHABLE, DECLINED, BAD_CONTACT (a human should
 *             look, this group is exactly ACTIONABLE_SALLY_STATES)
 *   closed  — OPTED_OUT, STAFFER_REJECTED, JOB_CLOSED
 *
 * OPTED_OUT is the one to be careful with: it's a do-not-contact warning, shown
 * so nobody texts them manually, and deliberately outside the owed group.
 */
export enum SallyState {
  PENDING_OUTREACH = "PENDING_OUTREACH",
  IN_CONVERSATION = "IN_CONVERSATION",
  CONVERTED = "CONVERTED",
  RULE_BLOCKED = "RULE_BLOCKED",
  UNREACHABLE = "UNREACHABLE",
  DECLINED = "DECLINED",
  BAD_CONTACT = "BAD_CONTACT",
  OPTED_OUT = "OPTED_OUT",
  STAFFER_REJECTED = "STAFFER_REJECTED",
  JOB_CLOSED = "JOB_CLOSED",
}

/**
 * The states worth a second, human attempt — the sourcing view's default
 * selection. OPTED_OUT is deliberately absent: those candidates asked us to
 * stop, so they're visible and badged but never offered as work.
 */
export const ACTIONABLE_SALLY_STATES: SallyState[] = [
  SallyState.RULE_BLOCKED,
  SallyState.UNREACHABLE,
  SallyState.DECLINED,
  SallyState.BAD_CONTACT,
];

/**
 * One candidate Sally owns on this job. From guac's direct read of the job's
 * sourced candidacies — not from search — so the set is complete regardless of
 * query, radius, or search's exclusion rules.
 */
export type SallyCandidate = {
  talentId: number;
  firstName: string | null;
  lastName: string | null;
  agenticScore: number | null;
  sallyState: SallyState;
  sallyStateReason: string | null;
  lastStatusUpdateAt: string;
};

export type SallyCandidates = {
  // Every state, best APR first. Nothing withheld — narrow via `counts` keys.
  candidates: SallyCandidate[];
  // Per-state totals. Every state is present, including zeros.
  counts: Record<SallyState, number>;
};

// A candidate's stance on one shift. There is no "unknown" member on purpose.
// Unknown is the absence of a row, not a value it can hold.
export enum ShiftPreferenceStatus {
  NO = "NO",
  PREFERRED = "PREFERRED",
  YES = "YES",
}
export enum ShiftType {
  FIRSTSHIFT = "FIRSTSHIFT",
  SECONDSHIFT = "SECONDSHIFT",
  THIRDSHIFT = "THIRDSHIFT",
  WEEKEND = "WEEKEND",
}

export enum SHORTCUT_SECTION_ID {
  GLOBAL = "Global",
  CANDIDATE_SHEET = "Candidate Sheet",
  SEARCH_RESULT_SHEET = "Search Result Sheet",
  JOB_UPDATE_MODAL = "Job Update Modal",
  COMPANY_JOB_INTAKE = "Company / Job Intake",
  REQUISITIONS = "Requisitions",
  JOB_PAGE = "Job Page",
}

export enum StaffingFirmCostStructure {
  FIXED = "FIXED",
  PERCENTAGE = "PERCENTAGE",
}

export enum StakeholderRole {
  BUYER = "BUYER",
  FINAL_DECISION_MAKER = "FINAL_DECISION_MAKER",
  HIRING_MANAGER = "HIRING_MANAGER",
  INFLUENCER = "INFLUENCER",
  INTERVIEWER = "INTERVIEWER",
  PRIMARY_CONTACT = "PRIMARY_CONTACT",
}

export enum StakeholderValuePerception {
  SPEED_COMMUNICATION = "SPEED_COMMUNICATION",
  SPEED_SUBMITTALS = "SPEED_SUBMITTALS",
  TRANSPARENCY = "TRANSPARENCY",
  QUALITY_SUBMITTALS = "QUALITY_SUBMITTALS",
  RELATIONSHIP_AND_TRUST = "RELATIONSHIP_AND_TRUST",
  COST = "COST",
  RETENTION = "RETENTION",
}

export enum TaskStatus {
  COMPLETED = "COMPLETED",
  INCOMPLETE = "INCOMPLETE",
  ALL = "ALL",
}

export enum TextAIOutreachStatus {
  INITIATED = "INITIATED",
  ACTIVE = "ACTIVE",
  CALL_SCHEDULED = "CALL_SCHEDULED",
  CALL_RESCHEDULED = "CALL_RESCHEDULED",
  CALL_COMPLETED = "CALL_COMPLETED",
  CALL_CANCELLED = "CALL_CANCELLED",
  CLOSED = "CLOSED",
  HOLD = "HOLD",
  STOP = "STOP",
  PROXY = "PROXY",
  HANDOFF = "HANDOFF",
}

export enum Timezone {
  EASTERN = "EASTERN",
  CENTRAL = "CENTRAL",
  MOUNTAIN = "MOUNTAIN",
  MOUNTAIN_ARIZONA = "MOUNTAIN_ARIZONA",
  PACIFIC = "PACIFIC",
}

export enum UserInvitationStatus {
  CANCELLED = "CANCELLED",
  EXPIRED = "EXPIRED",
  PENDING = "PENDING",
  ACCEPTED = "ACCEPTED",
}

export enum UserRole {
  PRODUCT = "PRODUCT",
  SALES = "SALES",
  STAFFER = "STAFFER",
  CSM = "CSM",
  ADMIN = "ADMIN",
}

export enum ViewTableName {
  JOB_PIPELINE = "JOB_PIPELINE",
  JOB_TRACKER = "JOB_TRACKER",
  MY_CANDIDACIES = "MY_CANDIDACIES",
  PLACEMENT_TRACKER = "PLACEMENT_TRACKER",
  TASK_TRACKER = "TASK_TRACKER",
}

export enum VoiceAIOutreachInitiationType {
  DIRECT = "DIRECT",
  SCHEDULED = "SCHEDULED",
}

export enum VoiceAIOutreachStatus {
  PENDING = "PENDING",
  IN_PROGRESS = "IN_PROGRESS",
  FAILED = "FAILED",
  COMPLETED = "COMPLETED",
  CANCELLED = "CANCELLED",
  VOICEMAIL = "VOICEMAIL",
}

// nova's jobSpecificSearch hydrates these from guac-derived types that aren't
// linked in IFE, so tRPC inference collapses them to `any`; spelled out
// manually and cast at the boundary.
export type SearchV2WorkExperience = {
  title: string | null;
  company: string | null;
  endYear?: string | null;
  years: number;
  matchedKeywords?: string[];
  matchedTitles?: string[];
};

export type SearchV2CandidacyData = {
  talentId: number;
  submissions: {
    title: string;
    company: string;
    source: CandidacySource | null;
    submittedAt: string;
    postSubmissionStatus: CandidacyStatus | null;
    postSubmissionReason: string | null;
    candidacyId: string;
    jobId: string;
    companyId: string;
  }[];
  schedulingAgentReachedOut: boolean;
  // Hydrated by guac.
  sallyState: SallyState | null;
  sallyStateReason: string | null;
  recentActivityAt: string | null;
  activePipelines: {
    title: string;
    company: string;
    status: CandidacyStatus;
    candidacyId: string;
    jobId: string;
    companyId: string;
  }[];
  currentPlacement: {
    title: string;
    company: string;
    tenure: string;
  } | null;
  lastCommunication: LastPing | null;
  // Latest comm the candidate sent us on any channel; null with outbound comms
  // present means they never replied. Optional until guac ships it.
  lastInboundCommunication?: LastPing | null;
  // 4 most recent SMS messages, newest first; absent pre-hydration.
  recentMessages?: LastPing[];
  candidacyStatus: CandidacyStatus | null;
  source: CandidacySource | null;
  agenticScore: number;
};

// PARAMETERS //

// this is for the pagination and search query for the demand page
export interface GetDemandCompaniesParams {
  limit: number;
  page: number;
  query: string;
}

// Mirror of guac's InterviewSortField enum — keep the two in sync (no drift).
export enum InterviewSort {
  CreatedAt = "createdAt",
  DateTime = "dateTime",
  Title = "title",
  UpdatedAt = "updatedAt",
}

export type InterviewQueryParams = {
  sort: InterviewSort;
  order: SortOrder;
  candidacyId?: string;
};

export type JobQueryParams = {
  bottlenecks: JobBottleneck[];
  difficulties: JobDifficulty[];
  groupBy?: JobTrackerGroupBy;
  healths: JobHealth[];
  jobStatuses: JobStatus[];
  order: SortOrder;
  ownerIds: number[];
  sort: JobTrackerSort;
  requisitionStatuses: RequisitionStatus[];
  urgencies: JobUrgency[];
};

export type LeaderboardQueryParams = {
  dateRange?: DateRange;
  order: SortOrder;
  sort: LeaderboardSort;
  timePeriod: DateFilterOption;
};

// * IMPORTANT: MyCandidaciesGroupBy values should be aligned with the enum values in my-candidacies-query-params.dto.ts in guac.
export enum MyCandidaciesGroupBy {
  Job = "job",
  Status = "status",
}

// * IMPORTANT: MyCandidaciesSortBy values should be aligned with the enum values in my-candidacies-query-params.dto.ts in guac.
export enum MyCandidaciesSortBy {
  LastPingAt = "lastPingAt",
  Status = "status",
  AgentScore = "agentScore",
}

export type MyCandidaciesQueryParams = {
  groupBy?: MyCandidaciesGroupBy;
  order: SortOrder;
  sort: MyCandidaciesSortBy;
  statuses: CandidacyStatus[];
  userId?: number | null;
  allUsers?: boolean;
};

// * IMPORTANT: groupByOptions values should be aligned with the enum values in placement-tracker-query-params.dto.ts in guac and be column names in the placement table in guac.
export enum PlacementTrackerGroupBy {
  Owner = "ownerUserId",
  Status = "status",
}

// * IMPORTANT: PlacementTrackerSortBy values should be aligned with the enum values in placement-tracker-query-params.dto.ts in guac and be column names in the placement table in guac.
export enum PlacementTrackerSortBy {
  CreatedAt = "createdAt",
  DateAccepted = "dateAccepted",
  DateOffered = "dateOffered",
  RevenueEarned = "revenueEarned",
}

export type PlacementQueryParams = {
  candidacyId?: string;
  dateAccepted?: DateRange;
  dateAcceptedFilter: DateFilterOption;
  groupBy?: PlacementTrackerGroupBy;
  order: SortOrder;
  ownerIds: number[];
  placementStatuses: PlacementStatus[];
  sort: PlacementTrackerSortBy;
};

export type RequisitionQueryParams = {
  order: SortOrder;
  sort: RequisitionSort;
  staffers: number[];
  statuses: RequisitionStatus[];
};

// * IMPORTANT: RequisitionSort values should be aligned with the enum values in requisition-table-query-params.dto.ts in guac and be column names in the requisition table in guac.
export enum RequisitionSort {
  CreatedAt = "createdAt",
  DateDue = "dateDue",
  DateProjectedClose = "dateProjectedClose",
}

export type ClassifiedKeyword = {
  category:
    | "Skills"
    | "Equipment"
    | "Processes"
    | "Industries"
    | "Certifications"
    | "Soft Skills"
    | "Safety"
    | null;
  color:
    | "blue"
    | "violet"
    | "teal"
    | "orange"
    | "green"
    | "slate"
    | "red"
    | null;
  keyword: string;
};

// Mirrors nova's `relevantTenure` search filter: years of experience counted
// within one position category. At least one bound is set when present.
export type SearchV2TenureFilter = {
  category: PositionCategory;
  floor?: number;
  ceiling?: number;
};

export type SearchV2Filters = {
  // When false/omitted the API excludes candidates placed anywhere or past the
  // submitted stage in any active pipeline; true includes them, sorted to the
  // bottom of each page.
  includePastSubmitted: boolean;
  includeRejected: boolean;
  keywords: ClassifiedKeyword[];
  /** Overrides the job's location as the search center; empty/absent uses the job's city/state. */
  location?: string;
  // Hide candidates whose desired pay is above this rate. Sent to the search
  // API as `payFloor` — the wire name predates the ceiling semantics.
  payCeiling: string;
  title: string[];
  // Optional: filters persisted before tenure existed won't carry the key.
  tenure?: SearchV2TenureFilter | null;
};

/** Ranking weights sent verbatim to the search API. */
export type SearchV2Weights = {
  activityWeight: number;
  semanticWeight: number;
  textWeight: number;
  weRecencyWeight: number;
};

export enum TaskPriority {
  NONE = "NONE",
  LOW = "LOW",
  MEDIUM = "MEDIUM",
  HIGH = "HIGH",
  URGENT = "URGENT",
}

export type TaskQueryParams = {
  assignees: number[];
  order: SortOrder;
  priority: TaskPriority[];
  sort: TaskSort;
  status: TaskStatus;
};

export enum TaskSort {
  CompletedDate = "completedDate",
  CreatedAt = "createdAt",
  DueDate = "dueDate",
  Priority = "priority",
}

export type ViewQueryParams = {
  tableName: ViewTableName;
  jobId?: string;
};

// REQUESTS //

export type BatchUpdateOrphanedAtsJobsRequest = {
  ids: string[]; // primary key of the ats job (not the atsJobId)
  laborupJobId: string;
};

export type CancelUserInvitationRequest = {
  inviteeEmail: string;
};

export type ClassifyCandidateSearchFiltersRequest = {
  query: string;
};

export type ClassifyKeywordColorsRequest = {
  keywords: string[];
};

export type HydrateCustomerTopPriorityWorkExperience = {
  company?: string | null;
  currentPosition?: boolean | null;
  descriptions?: string[] | null;
  endMonth?: string | null;
  endYear?: string | null;
  location?: string | null;
  startMonth?: string | null;
  startYear?: string | null;
  title?: string | null;
};

export type HydrateCustomerTopPriorityRequest = {
  jobId: string;
  userIds: number[];
};

export type HydrateCustomerTopPriorityResponse = Record<string, string>;

export type GenerateJobKeywordSuggestionsRequest = {
  qualifications: string;
};

export type SearchEventContext = {
  isTopCandidate?: boolean;
  rank?: number;
  resultCount?: number;
  searchMode?: "sally" | "standard";
  source?: "search_review_modal" | "search_results";
};

export type SearchInterestChangeOptions = {
  reason?: "shortlist_cleanup" | "undo" | "uninterested";
  searchAnalytics?: SearchEventContext;
};

export type CreateCandidacyOwnerRequest = {
  currentOwner?: number;
  newOwner: number;
};

export type CreateCandidacyRequest = {
  agentShouldReachOutIfEnabled?: boolean;
  searchAnalytics?: SearchEventContext;
  screeningAnswers?: string;
  source: CandidacySource;
  status: CandidacyStatus;
  talentId?: number | null;
};

export type CreateCommissionLedgerEntryRequest = {
  placementId: number;
  type: CommissionLedgerEntryType;
  amount: number;
  commissionRate?: number;
  reason?: string;
};

export type CreateCompanyFileRequest = {
  companyFile: File;
  isLogo?: boolean;
  isSmallLogo?: boolean;
  name: string;
};

export type CreateCompanyNoteRequest = {
  title: string;
  content: string;
};

export type CreateCompanyRequest = {
  name: string;
};

export type CreateCertificationRequest = {
  name: string;
  userId: number;
  organization?: string;
  credentialId?: string;
  credentialUrl?: string;
  endMonth?: string;
  endYear?: string;
  startMonth?: string;
  startYear?: string;
};

export type CreateJobFileRequest = {
  jobFile: File;
  name: string;
};

export type CreateJobNoteRequest = {
  title: string;
  content: string;
};

export type CreateJobDifficultyUrgencyRequest = {
  difficulty: JobDifficulty;
  urgency: JobUrgency;
};

export type CreateJobIntakeRequest = Omit<
  JobIntake,
  "id" | "jobId" | "stakeholders" | "createdAt" | "updatedAt"
>;

export type CreateJobUpdateRequest = {
  bottleneck?: JobBottleneck | null;
  description?: string;
  health?: JobHealth;
  difficulty?: JobDifficulty | null;
  urgency?: JobUrgency;
};

export type CreateCandidacyPlacementRequest = {
  baseCompensation: number;
  candidacyId: string;
  dateAccepted: string | null;
  dateOffered: string;
  dateSetToStart: string | null;
  retentionBonusDays: number | null;
  jobId: string;
  ownerUserId: number;
  requisitionId: number;
  retentionBonusAmount: number | null;
  revenueEarned: number | null;
  shiftDifferential: number | null;
  shiftType: ShiftType;
  shiftTypeDescription: string | null;
  status: PlacementStatus;
};

export type CreateInternalUserRequest = {
  email: string;
  firstName: string;
  lastName: string;
  role?: Role;
  userRole?: UserRole;
};

export type CreateOrUpdateCampaignRequest = {
  name: string;
  internalPhoneNumberId: string;
  payload: CampaignPayload;
  sentAt?: string;
};

export type CreateOrUpdateCompanyCommentRequest = {
  content: JSONContent;
};

export type CreateOrUpdateCompanyInformationRequest = {
  companyInformation: {
    companyAbbreviation: string;
    logoCompanyFileId: number | null;
    smallLogoCompanyFileId?: number | null;
    address: string | null;
    description: string | null;
    naicsCodes: string[];
    customerIndustries: string[];
    manufacturingCapabilities: string[];
    equipment: string[];
    materials: string[];
    headcount: number | null;
    foundedYear: string | null;
    location: string | null;
    dunsNumber: string | null;
    cageCode: string | null;
    paymentTerms: PaymentTerms;
    websiteUrl: string | null;
  };
};

export type CreateOrUpdateDemoConfigRequest = {
  title: string;
  config: {
    autoFillJobUrl: string;
    hideCandidatesForCompanies: string[];
    region: DemoRegion;
    position: DemoPosition;
    stevie: StevieDemoConfig;
  };
};

export type CreateOrUpdateInternalCandidacyCommentRequest = {
  contentJson: JSONContent;
  threadId?: number;
};

export type CreateExternalCandidacyCommentRequest = {
  content: string;
};

export type CreateOrUpdateJobCommentRequest = {
  content: JSONContent;
};

export type CreateOrUpdateJobRequest = {
  address: string;
  backfillGuaranteeDays?: number | null;
  benefits: string | null;
  city: string;
  compensationMax: number;
  compensationMin: number;
  compensationType: CompensationType;
  employmentType: EmploymentType;
  estimatedRevenueAmount?: number | null;
  isOpenToRelocation: boolean;
  keywords: JobKeyword[];
  overview: string;
  percentOfSalary?: number | null;
  positionCategory: PositionCategory | null;
  qualifications: string;
  responsibilities: string;
  revenueType?: RevenueType;
  screeningQuestions?: { screeningQuestions: ScreeningQuestion[] } | null; // Can be `undefined` because screening questions are managed in a different section.
  shiftDifferentialMin?: number | null;
  shiftDifferentialMax?: number | null;
  shiftEnd: string | null;
  shiftStart: string | null;
  shiftType: ShiftType[];
  state: string;
  status: JobStatus;
  title: string;
  topCustomerPriority: string | null;
  zipCode: string;
};

export type CreateOrUpdateSignupVariantRequest = {
  name: string;
  payload: SignupVariantPayload | null;
};

export type CreateUserRequest =
  RouterInput["internal"]["user"]["active"]["create"];

export type CreateRequisitionRequest = {
  shiftDifferentialMin: number | null;
  shiftDifferentialMax: number | null;
  shiftType: ShiftType[];
  status: RequisitionStatus;
};

export type CreateRequisitionOwnerRequest = {
  requisitionId: number;
  userId: number;
};

export type CreateSourcingRequestRequest = {
  location: string;
  radiusMiles: number;
  searchTerms: SearchTerms;
  maxMessages: number;
  minimumScore: number;
};

export type CreateStakeholderRequest = {
  companyId: string;
};

export type CreateTaskRequest = {
  assignees: number[];
  dueDate: string;
  priority?: TaskPriority;
  title: string;
};

export type CreateUserInvitationRequest = {
  firstName: string;
  lastName: string;
  inviteeEmail: string;
};

export type CreateViewRequest = {
  tableName: ViewTableName;
  queryParams: QueryParamsType;
  jobId?: string;
};

export type CreateVoiceAIOutreachRequest = {
  scheduledDateTime?: string;
};

export type DecideJobPauseRequestRequest = {
  approved: boolean;
};

export type DeleteRequisitionOwnerRequest = CreateRequisitionOwnerRequest;

export type DuplicateJobRequest = {
  linkToSource?: boolean;
};

export type LinkJobRequest = {
  targetJobId: string;
};

export type ParseScreeningQuestionsAndAnswersRequest = {
  inputText: string;
};

export type PauseJobRequest = {
  pauseReason: string;
  pauseType: JobPauseType;
};

export type ResendUserInvitationRequest = {
  inviteeEmail: string;
};

export type SearchSession = {
  id: string;
  userId: number;
  jobId: string;
  startedAt: string;
  endedAt: string | null;
  durationMs: number | null;
  // Set when the session closes; null while open.
  interestCount: number | null;
  shortlistCount: number | null;
  createdAt: string;
  updatedAt: string;
};

export type SearchSessionStats = {
  avgSessionTimeMs: number | null;
  sessionCount: number;
  interestCount: number;
  lastSessionInterestCount: number;
  lastSessionShortlistCount: number;
};

export type SearchSessionGoals = {
  weeklyInterestCount: number;
  weeklyShortlistCount: number;
};

// A shortlist recorded during the live search session, with the pet coins the
// server awarded for it — feeds the session panel's coin total and breakdown.
export type SessionShortlist = {
  candidateName: string;
  coinsAwarded: number;
  // Weekly shortlist goal bonus paid on this shortlist, if it crossed the goal.
  goalBonusCoins: number;
};

export enum SearchDecision {
  HIDDEN = "HIDDEN",
  INTERESTED = "INTERESTED",
  UNINTERESTED = "UNINTERESTED",
}

// The most recent interest/hide made in the search list, with a closure that
// reverts it. Feeds the session panel's rear-view row.
export type SearchLastDecision = {
  candidateName: string;
  decision: SearchDecision;
  talentId: number;
  undo: () => void;
};

// Coins the session's interests earned: the per-10 milestone coins and the
// weekly interest goal bonus, both paid server-side on the interest upsert.
export type SessionInterestCoins = {
  coins: number;
  goalBonusCoins: number;
};

export type StartSearchSessionResponse = {
  session: SearchSession;
  stats: SearchSessionStats;
  goals: SearchSessionGoals;
};

export type UpsertSearchInterestRequest = {
  isInterested: boolean;
  jobId: string;
  searchAnalytics?: SearchEventContext;
  sessionId?: string;
  talentId: number;
};

export type DeleteSearchInterestRequest = {
  jobId: string;
  reason?: "shortlist_cleanup" | "undo" | "uninterested";
  searchAnalytics?: SearchEventContext;
  talentId: number;
};

export type SendEmailRequest = {
  subject: string;
  body: string;
  candidacyId?: string;
  talentId?: number;
  inReplyTo?: {
    gmailThreadId: string;
    rfcMessageId: string;
  };
};

export type SendQuoMessageRequest = {
  body: string;
  phone: string;
  talentId?: number | null;
  candidacyId?: string;
  sourcedCandidacyId?: string;
};

export type UpdateCallRequest = {
  candidacyId?: string;
  userId?: number;
};

export type UpdateCandidacyRequest = {
  agentShouldReachOut?: boolean;
  evaluationNew?: CandidacyEvaluationNew;
  evaluations?: CandidacyEvaluation[];
  fromStatus?: CandidacyStatus;
  jobId?: string;
  rejectedReason?: CandidacyRejectionReason;
  stafferAgentActive?: boolean;
  status?: CandidacyStatus;
  statusChangeReason?: string | null;
  summary?: string;
  viaScreened?: boolean; // additional status_change for screened needed before update to new status
};

export type MoveCandidacyRequest = {
  agentShouldReachOut?: boolean;
  jobId: string;
  status?: CandidacyStatus;
};

export type UpdateCandidacyStatusChangeRequest = {
  userId: number;
};

export type UpdateCompanyNoteRequest = {
  title?: string;
  content?: string;
};

export type UpdateCompanyIntakeRequest = {
  ownerId?: number | null;
  general?: CompanyIntakeGeneral;
  sales?: CompanyIntakeSales;
  sites?: CompanyIntakeSite[];
  talentRecruitingHistory?: CompanyIntakeTalentRecruitingHistory;
  culture?: CompanyIntakeCulture;
  business?: CompanyIntakeBusiness;
  attachments?: CompanyIntakeAttachment[];
};

export type UpdateJobIntakeRequest = {
  generalInfo?: Partial<JobIntakeGeneralInfo>;
  talentRecruitingHistory?: Partial<JobIntakeTalentRecruitingHistory>;
  culture?: Partial<JobIntakeCulture>;
  idealCandidateProfiles?: IdealCandidateProfile[];
  antiIdealCandidateProfiles?: AntiIdealCandidateProfile[];
  laborupRound?: JobIntakeLaborupRound;
  interviewProcess?: InterviewRound[];
  datesAndUrgency?: Partial<JobIntakeDatesAndUrgency>;
  screeningQuestions?: JobIntakeScreeningQuestions;
  attachments?: JobIntakeAttachment[];
};

export type UpdateCompanyInternalConfigRequest = {
  // Replaces the whole list.
  relatedCompanyNames?: string[];
};

export type UpdateJobInternalConfigRequest = {
  evaluations?: EvaluationBuild[];
  isAgentFlowEnabled?: boolean;
  agenticStaffingPrompt?: string | null;
  autoProfileRatingHumanInput?: string | null;
  autoProfileRatingRubric?: string | null;
  prefilledDefaultPrompt?: string | null;
  category?: JobCategory;
  ctdReqs?: number | null;
  stafferCallEvaluationPrompt?: string | null;
  stevieOutreachMaxRating?: number;
  voiceAiStaffingDynamicQuestions?: VoiceAiStaffingDynamicQuestion[];
  ownerId?: number | null;
  isWorkableJobPosted?: boolean;
  isSponsoredOnIndeed?: boolean;
  stafferAgentActive?: boolean;
  // Action-only: scope a resume kickoff to a single owner. Omit to resume all.
  resumeForOwnerUserId?: number;
};

export type UpdateJobManagerRequest = {
  jobId: string;
  userId: number;
  isManager: boolean;
};

export type UpdateJobCtdReqsRequest = {
  companyId: string;
  jobId: string;
  jobInternalConfigId: string;
  ctdReqs: number | null;
};

export type UpdateJobStafferRequest = {
  jobId: string;
  userId: number;
  isSelected: boolean;
};

export type UpdateJobNoteRequest = {
  title?: string;
  content?: string;
};

export type UpdateNotificationsRequest = {
  notificationIds: number[];
  dismiss?: boolean;
  markAsRead?: boolean;
};

export type UpdateUserNotificationPreferenceRequest = {
  inAppEnabled?: boolean;
  emailEnabled?: boolean;
  slackEnabled?: boolean;
};

export type UpdateUserPushPreferenceRequest = {
  mode: PushMode;
};

export type UpdateCertificationRequest = {
  name?: string;
  organization?: string | null;
  credentialId?: string | null;
  credentialUrl?: string | null;
  endMonth?: string | null;
  endYear?: string | null;
  startMonth?: string | null;
  startYear?: string | null;
};

export type UpdatePendingUserRequest = {
  currentPay?: number | null;
  desiredPay?: number;
  isOpenToRelocation?: boolean;
  laborupProRank?: LaborupProRank | null;
  linkedinUrl?: string | null;
  location?: string;
  notes?: string | null;
  permanentStatus?: string | null;
  primaryPendingResumeId?: number;
  professionalSummary?: string | null;
};

export type UpdateCandidacyPlacementRequest =
  Partial<CreateCandidacyPlacementRequest> & {
    dateEmploymentEnded?: string | null;
    dateRejected?: string | null;
    dateStarted?: string | null;
    employmentEndedReason?: string | null;
  };

export type UpdateProfileUserRequest = {
  currentPay?: number | null;
  desiredPay?: number;
  desiredPosition?: string;
  location?: string;
  permanentStatus?: string | null;
};

export type UpdateRequisitionRequest = Partial<CreateRequisitionRequest> & {
  dateDue?: string | null;
  dateFilled?: string | null;
  dateLost?: string | null;
  dateProjectedClose?: string | null;
  dateRequested?: string | null;
  dateStarted?: string | null;
  retentionBonusDays?: number | null;
  lostReason?: string | null;
  ownerUserId?: number | null;
  retentionBonusAmount?: number | null;
  shiftTypeDescription?: string | null;
};

export type UpdateInternalUserRequest = {
  firstName?: string;
  lastName?: string;
  email?: string;
  role?: Role;
  userRole?: UserRole;
  lastActiveAt?: string;
  lastPage?: string;
};

export type UpdateStakeholderRequest = {
  firstName?: string | null;
  lastName?: string | null;
  title?: string | null;
  email?: string | null;
  phone?: string | null;
  linkedinUrl?: string | null;
  role?: StakeholderRole[] | null;
  personalityAndNotes?: string | null;

  communicationMode?: CommunicationMode[] | null;
  primaryContact?: boolean;
  ccOnEmails?: boolean;
  communicationStyle?: string | null;
  communicationFrequency?: CommunicationFrequency | null;
  communicationDetails?: string | null;

  valuePerception?: StakeholderValuePerception[];
  valuedCandidateQualities?: string | null;
  commonRejectionReasons?: string | null;

  viewOnExternalPartners?: string | null;
};

export type UpdateTaskRequest = {
  completedDate?: string | null;
  dueDate?: string | null;
  priority?: TaskPriority;
  title?: string;
};

export type UpsertPetRequest = {
  // Admin grants include a pet version so the server rejects stale writes.
  expectedUpdatedAt?: string;
  pet: Pet;
};

export type RedeemIncentiveRequest = {
  // The incentive's title; stored on the ledger row for whoever fulfills it.
  description: string;
};

export type AdjustPetCoinsRequest = {
  refundUnlocks?: {
    petIds?: string[];
    colorIds?: string[];
    topperIds?: string[];
    accessoryIds?: string[];
  };
  purchaseTransactionId?: number;
  amount: number;
  operation: "GRANT" | "REFUND";
  description: string;
  requestId: string;
  expectedUpdatedAt: string;
};

export type PurchasePetRequest = {
  // Coins to spend; the server does the atomic balance check and ledger write.
  amount: number;
  description: string;
  // Cosmetic state after the purchase (unlocks, equips, offsets); ledger and
  // balance fields are ignored by the server.
  pet: Pet;
};

export type UpdateStafferEmployeeReportsRequest = {
  receivesStafferEmployeeReports: boolean;
};

export type StafferEmployeeReportsResponse = {
  userId: number;
  receivesStafferEmployeeReports: boolean;
};

export type UpdateUserRequest = {
  isOpenToRelocation?: boolean;
  laborupProRank?: LaborupProRank | null;
  linkedinUrl?: string | null;
  notes?: string | null;
  permanentStatus?: string | null;
  primaryResumeId?: number;
  professionalSummary?: string | null;
};

export type UpdateViewRequest = {
  queryParams?: QueryParamsType;
  name?: string;
};

export type UpsertJobUserSnapshotRequest = {
  jobId: string;
  appliedCount?: number;
  commsToSendCount?: number;
};

// The tapbacks a worker's phone can send, iMessage's own.
export type SadieSimulatorTapback =
  | "love"
  | "like"
  | "dislike"
  | "laugh"
  | "emphasize"
  | "question";

export type SadieSimulatorStartRequest = {
  talentId: number;
  // Null runs the usual model.
  model: string | null;
};

export type SadieSimulatorTextRequest = {
  talentId: number;
  body: string;
};

export type SadieSimulatorPromptChangesRequest = {
  talentId: number;
  // Every change, in the order they apply. Empty clears them.
  changes: SadieSimulatorPromptChange[];
};

export type SadieSimulatorTapbackRequest = {
  talentId: number;
  quoId: string;
  reaction: SadieSimulatorTapback;
};

// RESPONSES //

// One archived Staffer-assistant voice call. The url is a signed GCS read
// link that expires an hour after the list was fetched.
export type VoiceRecording = {
  sessionId: string;
  userId: number;
  stafferName: string | null;
  date: string;
  recordedAt: string | null;
  sizeBytes: number;
  contentType: string | null;
  extension: string;
  url: string;
};

// Where the worker app anchors its cover crops, in percent from the top-left.
// Same semantics as CSS `object-position` and expo-image `contentPosition`.
export type ExploreImageFocus = { x: number; y: number };

// The credit a licensed explore image needs wherever it's shown. `credit` and
// `license` make the short line ("Photo: credit · license"); the rest is for a
// full credits page.
export type ExploreImageAttribution = {
  credit: string;
  license: string;
  licenseUrl: string;
  sourceUrl: string;
  text: string;
};

// Mirrors guac's explore image industries.
export enum ExploreImageIndustry {
  AEROSPACE_AND_DEFENSE = "AEROSPACE_AND_DEFENSE",
  AUTOMOTIVE_AND_EV = "AUTOMOTIVE_AND_EV",
  SEMICONDUCTORS_AND_ELECTRONICS = "SEMICONDUCTORS_AND_ELECTRONICS",
  ROBOTICS_AND_INDUSTRIAL_MACHINERY = "ROBOTICS_AND_INDUSTRIAL_MACHINERY",
  FOOD_BEVERAGE_AND_CONSUMER_GOODS = "FOOD_BEVERAGE_AND_CONSUMER_GOODS",
  MEDICAL = "MEDICAL",
  ENERGY_AND_NUCLEAR = "ENERGY_AND_NUCLEAR",
  CONSTRUCTION_AND_DATA_CENTERS = "CONSTRUCTION_AND_DATA_CENTERS",
  LOGISTICS_AND_DISTRIBUTION = "LOGISTICS_AND_DISTRIBUTION",
  BUILDING_PRODUCTS = "BUILDING_PRODUCTS",
  MACHINE_AND_FABRICATION_SHOPS = "MACHINE_AND_FABRICATION_SHOPS",
  STEEL_AND_METAL = "STEEL_AND_METAL",
  CHEMICALS_AND_MATERIALS = "CHEMICALS_AND_MATERIALS",
  SHIPBUILDING = "SHIPBUILDING",
  MARINE = "MARINE",
}

// A worker app explore image. It lives once in the private explore bucket and
// is listed in the manifest of each of its position categories. The url is a
// signed read link that expires an hour after the list was fetched.
export type ExploreImage = {
  id: string;
  width: number;
  height: number;
  focus: ExploreImageFocus;
  // Short description the admin gallery searches; null until someone adds one.
  caption: string | null;
  // Lowercase search terms, e.g. "tig welding".
  keywords: string[];
  // Industries the scene suits; empty when it's generic.
  industries: ExploreImageIndustry[];
  // Null for photos we own or that need no credit.
  attribution: ExploreImageAttribution | null;
  url: string;
  sizeBytes: number;
  positionCategories: PositionCategory[];
};

// Guac's proposed details for a photo about to be uploaded.
export type ExploreImageSuggestion = {
  // The one category the photo best suits; empty when the model was unsure.
  positionCategories: PositionCategory[];
  caption: string;
  keywords: string[];
  industries: ExploreImageIndustry[];
  // Why the photo may not suit a job card; null when it does.
  poorFitReason: string | null;
};

// A templated agent send that is queued but not yet delivered, so the staffer
// can read what the candidate is about to get and when.
export type ScheduledAgentMessageResponse = {
  id: string;
  candidacyId: string;
  channel: StafferAgentScheduledMessageChannel;
  kind: StafferAgentScheduledMessageKind;
  stepIndex: number;
  body: string;
  scheduledFor: string;
};

export type StafferTrophyLeader = {
  achievedAt: string;
  screenCount?: number;
  firstName: string;
  lastName: string;
  rank: number;
  unit: TrophyValueUnit | null;
  userId: number;
  value: number;
};

export type StafferTrophyAward = {
  leaders: StafferTrophyLeader[];
  minimumScreenCount?: number;
  type: TrophyAwardType;
};

export type StafferTrophiesResponse = {
  awards: StafferTrophyAward[];
};

export type StafferEarningsResponse = {
  commissionTiers: EarningsCommissionTier[];
  totalRevenueYtd: number;
  totalCommissionYtd: number;
  outstandingOffers: EarningsOffer[];
  placements: EarningsPlacement[];
  jobs: EarningsJob[];
};

export type CallHistoryAiCall = {
  id: string;
  candidacyId: string | null;
  job: {
    title: string;
    company: string;
  };
  scheduledDateTime: string;
  status: string;
  initiationType: string;
  audioUrl: string | null;
  transcript: unknown | null;
  aiSummary: string | null;
  aiSummaryJson: unknown | null;
  screeningSummaryJson: unknown | null;
  callStructuredSummary: CallStructuredSummary | null;
};

export type CallHistoryStafferCall = {
  id: string;
  candidacyId: string | null;
  quoId: string;
  startedAt: string;
  status: string;
  direction: CallDirection;
  duration: number | null;
  phone: string | null;
  audioUrl: string | null;
  transcript: unknown | null;
  // Quo's structured summary — bullet arrays, not the rich-text HTML string
  // AI calls carry.
  summary: CallSummary | null;
  evaluation: unknown | null;
  user: { firstName: string; lastName: string };
};

export type CallHistoryResponse = {
  aiCalls: CallHistoryAiCall[];
  stafferCalls: CallHistoryStafferCall[];
};
export type CandidateSearchFilters = {
  keywords?: ClassifiedKeyword[];
  payFloor?: string;
  weTitle?: string[];
};

export type ClassifyKeywordColorsResponse = ClassifiedKeyword[];

export type GenerateCandidacyArtifactsResponse = {
  packetUrl: string | null;
  resumeFileId: number | null;
};

export type GenerateJobKeywordSuggestionsResponse = {
  keywords: JobKeyword[];
};

export type AtsJobResponse = AtsJob & {
  orphanedCandidatesCount: number;
};

// A member of a job's link group ("versions of the same job"). CLOSED jobs
// are included by design — the primary link target is usually a closed job.
export type LinkedJobResponse = {
  companyId: string;
  createdAt: string;
  id: string;
  pausedAt: string | null;
  pauseReason: string | null;
  pauseType: JobPauseType | null;
  status: JobStatus;
  title: string;
};

// Internal sourcing is split by agent: Sally works 4-5 star matches, Stevie
// (the text agent) works 1-3 star matches.
export type InternalSourcingFunnels = {
  sally: SourcingFunnelCounts;
  stevie: SourcingFunnelCounts;
};

export type ExternalSourcingFunnel = SourcingFunnelCounts & {
  hasTicket: boolean;
  isActive: boolean;
};

export type ZipRecruiterSourcingFunnel = SourcingFunnelCounts & {
  hasTicket: boolean;
  isActive: boolean;
};

// The outside resume databases a staffer can drop resumes from. Mirrors the
// external subset of guac's SourcedCandidacySource: the internal sources are
// not something anyone hands us a file for.
export enum ExternalResumeSource {
  JUICEBOX_RESUME_UNLOCK = "JUICEBOX_RESUME_UNLOCK",
  MONSTER_RESUME_UNLOCK = "MONSTER_RESUME_UNLOCK",
  ZIPRECRUITER_RESUME_UNLOCK = "ZIPRECRUITER_RESUME_UNLOCK",
}

export type CreateSourcingResumeBatchRequest = {
  resumes: File[];
  source: ExternalResumeSource;
};

export type SourcingResumeBatch = {
  id: string;
  jobId: string;
  source: ExternalResumeSource;
  fileCount: number;
};

export type RecomputeCommuteResponse = {
  commuteDistanceMiles: number | null;
  commuteTimeMins: number | null;
};

export type SourcingFunnelCounts = {
  found: number;
  contacted: number;
  interested: number;
  notInterested: number;
};

export type ActiveScreeningPrompt = {
  interviewId: number;
  candidacyId: string;
  companyId: string;
  jobId: string;
  dateTime: string;
  durationMinutes: number | null;
  firstName: string | null;
  lastName: string | null;
  talentId: number | null;
  jobTitle: string;
  companyName: string;
};

/**
 * A pause/cover request still awaiting a decision. Polled like an in-progress
 * screen rather than delivered as a notification — it is a live entity that
 * leaves the corner stack when it stops being PENDING.
 */
/** A job's pause flags on their own, for the pipeline move gate. */
export type JobPauseState = {
  id: string;
  pausedAt: string | null;
  pauseType: JobPauseType | null;
};

export type PendingPauseRequest = {
  requestId: number;
  jobId: string;
  companyId: string;
  companyName: string;
  jobTitle: string;
  pauseType: JobPauseType;
  pauseReason: string;
  stafferName: string;
  // Prose summary of what parking the job would strand, built by guac.
  pipelineState: string;
  createdAt: string;
};

export type ShortlistStafferOwnership = {
  userId: number;
  ownedCount: number;
};

export type ShortlistCandidaciesSegmentedByOwnership = {
  unownedCount: number;
  currentUserId: number;
  staffers: ShortlistStafferOwnership[];
};

// Coins are awarded server-side; a mutation that earned one carries the award
// back so the app can play the celebration without refetching the ledger.
export type StafferPetAwardResponse = {
  celebration: StafferPetCelebration;
  coinsAwarded: number;
} | null;

// The playyard's shared sky (Nashville), cached server-side for an hour.
// Sunrise/sunset are ISO instants for today in the yard's timezone.
export type PetYardWeatherResponse = {
  locationLabel: string;
  timezone: string;
  condition: PetYardWeatherCondition;
  weatherCode: number;
  temperatureF: number;
  windSpeedMph: number;
  isDay: boolean;
  sunrise: string;
  sunset: string;
  fetchedAt: string;
} | null;

export type UpdateCandidacyResponse = Candidacy & {
  candidacyOwners: { id: number; userId: number }[];
  stafferPetAward?: StafferPetAwardResponse;
};

export type CreateCandidacyResponse = {
  id: string;
  stafferPetAward?: StafferPetAwardResponse;
  // Weekly shortlist goal bonus, separate from the shortlist coin itself.
  stafferPetGoalAward?: StafferPetAwardResponse;
};

export type CallResponse = Call & {
  user: { firstName: string; lastName: string };
};

// A Sadie call, plus the number it came from (guac: /admin/relationships/calls).
export type SadieCallResponse = CallResponse & {
  from: string | null;
};

export type CampaignResponse =
  RouterOutput["internal"]["admin"]["campaigns"]["findAll"][number];

export type CancelUserInvitationResponse = {
  success: true;
};

// The hydration fields mirror the search-card hydrate response (hydrateSearch)
// and are absent when the candidacy has no talentId yet (pending talent).
// Screening Q&A from one of the talent's other candidacies, derived from
// GetOtherCandidaciesResponse rows for the "past apps" views.
export type PastApplicationAnswers = {
  candidacyId: string;
  companyName: string;
  createdAt: string;
  jobTitle: string;
  screeningAnswers: ScreeningAnswer[];
};

export type CandidacyInfoResponse = {
  id: string;
  jobId: string;
  status: CandidacyStatus;
  source: CandidacySource | null;
  isExternallySourced: boolean;
  isAutoResurrected: boolean;
  isTopPriority: boolean;
  lastStatusChangeByAgent: boolean;
  candidacyOwners: { id: number; userId: number }[];
  talentId?: number;
  submissions?: {
    title: string;
    company: string;
    source?: CandidacySource | null;
    submittedAt: string;
    // Only hydrated by search; the candidacy-info endpoint doesn't return these.
    postSubmissionStatus?: CandidacyStatus | null;
    postSubmissionReason?: string | null;
    candidacyId: string;
    jobId: string;
    companyId: string;
  }[];
  schedulingAgentReachedOut?: boolean;
  recentActivityAt?: string | null;
  activePipelines?: {
    title: string;
    company: string;
    status: CandidacyStatus;
    candidacyId: string;
    jobId: string;
    companyId: string;
  }[];
  lastAiCommunication?: LastPing | null;
  lastCommunication?: LastPing | null;
  lastHumanCommunication?: LastPing | null;
  candidacyStatus?: CandidacyStatus | null;
  agenticScore?: number | null;
  currentPlacement?: {
    title: string;
    company: string;
    tenure: string | null;
  } | null;
  aiSummary: string | null;
  commuteDistanceMiles: number | null;
  commuteTimeMins: number | null;
  // Deprecated: use aiCallInsights.motivation. Kept until guac drops it.
  motivation: string | null;
  motivationDetail?: string | null;
  motivationNeedsFollowUp?: boolean;
  // Quote-backed evidence from the latest Stevie call summary. Display-only:
  // rendered like paper-trail citations but never written to the paper trail.
  // Null when no summarized call exists. Citations null when the summary
  // predates quote capture.
  aiCallInsights?: {
    motivation: {
      value: string;
      citation: AiCallInsightCitation | null;
    } | null;
    locationAndCommute: {
      notes: string;
      isCommuteAcceptable: boolean | null;
      citation: AiCallInsightCitation | null;
    } | null;
  } | null;
  shift?: {
    detail: string | null;
    summary: string | null;
  } | null;
  drugAndBackground?: {
    summary: string | null;
  } | null;
  quoConversationId: string | null;
  createdAt: string;
  atsJobTitleAndLocation: string | null;
  screeningAnswers: { screeningAnswers: ScreeningAnswer[] } | null;
  // Recruiter-facing exec summary — distinct from aiSummary.
  summary: string | null;
  execSummaryPacketUrl: string | null;
  execSummaryPacketGeneratedAt: string | null;
  stafferAgentActive: boolean;
  // The candidacy's phone-of-record — quo threads are keyed on it, so sends
  // must use it over the live nova profile phone. Optional until the guac
  // /info endpoint returns it.
  phone?: string | null;
  textAIOutreachWithProxy: TextAIOutreachWithProxy | null;
  // Null when the candidacy has no status change yet.
  lastCandidacyStatusChange: {
    fromStatus: CandidacyStatusChange["fromStatus"];
    toStatus: CandidacyStatusChange["toStatus"];
    statusChangeReason: CandidacyStatusChange["statusChangeReason"];
    byAgent: CandidacyStatusChange["byAgent"];
  } | null;
  // From the latest agentic score change; byUserId non-null = human override.
  agenticScoreJustification: string | null;
  agenticScoreByUserId: number | null;
  eligibilityForStevieOutreach: StevieRouteEligibility;
  // Set when a staffer routed this candidacy through Stevie; the call end lands
  // once the candidate takes the call.
  stevieNudgedAt: string | null;
  stevieCallEndedAt: string | null;
  // Same pair the pipeline rows feed StevieCallIndicators: whole minutes on the
  // latest real Stevie screen, and the pre-call score when it raised the band.
  stevieCallDuration: number | null;
  elevatedFrom: number | null;
  // Sally converted this candidacy and is no longer talking, so a staffer can
  // have her text the candidate that they've been trying to reach them.
  sallyNudgeEligible: boolean;
  // When Sally last sent that nudge, or null. Once a day at most.
  sallyNudgedAt: string | null;
};

export type CandidacyWithProfileResponse = {
  activePlacement: ActivePlacement | null;
  atsJobTitleAndLocation: string | null;
  candidacy: Candidacy;
  candidacyAgenticScoreChanges: CandidacyAgenticScoreChange[];
  candidacyInterest: { isInterested: boolean; note: string } | null; // TODO: Why is this different from the other side?
  candidacyOwners: CandidacyOwner[];
  candidateProfile: TransformedCandidateDocument;
  commuteInfo: CommuteInfo;
  lastCandidacyStatusChange: {
    fromStatus: CandidacyStatusChange["fromStatus"];
    toStatus: CandidacyStatusChange["toStatus"];
    statusChangeReason: CandidacyStatusChange["statusChangeReason"];
    byAgent: CandidacyStatusChange["byAgent"];
  };
  lastPing: LastPing | null;
  referrer: {
    referrerFirstName: string | null;
    referrerLastName: string | null;
    referrerEmail: string | null;
    referrerPhone: string | null;
  } | null;
  source: CandidacySource | null;
  timeInStage: string;
  textAIOutreachWithProxy: TextAIOutreachWithProxy | null;
  voiceAIOutreachSlim: VoiceAIOutreachSlim | null;
};

// Minimal types for pipeline table performance optimization
export type MinimalCandidacyStatusChange = {
  createdAt: string;
  fromStatus: CandidacyStatus | null;
  toStatus: CandidacyStatus;
  statusChangeReason: string | null;
  byAgent: boolean;
};

export type MinimalScore = {
  toScore: number;
  fromScore: number | null;
  createdAt: string;
  justification: string | null;
  userId: number | null; // userId means human staffer overrode agentic rating
};

export type MockResume = {
  id: number;
  name: string;
  url: string;
  mimetype: string;
  rating: number | null;
  justification: string | null;
};

export type MinimalCandidacy = {
  id: string;
  createdAt: string;
  talentId: number | null;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  mostRecentJobTitle: string | null;
  mostRecentCompanyName: string | null;
  location: string | null;
  lastCandidacyStatusChange: MinimalCandidacyStatusChange;
  agenticScores: MinimalScore[];
  owners: { id: number; userId: number }[];
  voiceAiOutreach: VoiceAIOutreachSlim | null;
  source: CandidacySource | null;
  isExternallySourced: boolean;
  isAutoResurrected: boolean;
  contactsAttempted: number | null;
  stevieCallDuration: number | null;
  elevatedFrom: number | null;
  nextActionAt: string | null;
  commuteInfo: CommuteInfo;
  lastPing: LastPing | null;
  quoConversationId: string | null;
  phone: string | null;
  stafferAgentActive: boolean;
  nextScheduledAgentMessageAt: string | null;
  stevieNudgedAt: string | null;
  stevieCallEndedAt: string | null;
};

export type CompanySummary = {
  id: string;
  name: string;
  companyIntake: CompanyIntake | null;
  companyInternalConfig: {
    relatedCompanyNames: string[];
  };
  stakeholders: Stakeholder[];
  jobs: {
    id: string;
    title: string;
    status: JobStatus;
    stafferAgentActive: boolean;
  }[];
};

export type CompanyFileWithSignedUrlResponse = CompanyFile & {
  signedUrl: string;
};

export type DeleteManyResponse = {
  count: number;
};

export type DemandCompaniesResponse = {
  companies: DemandCompany[];
  hasMore: boolean;
  limit: number;
  page: number;
  totalPages: number;
  total: number;
};

export type GenerateSourcingBooleanQueryResponse = {
  booleanSearch: string;
};

export type GetCompanyInformationResponse = {
  companyInformation: CompanyInformation | null;
};

export type GetJobResponse = Job & {
  candidacyOwnerRotation: { rotation: number[] } | null;
};

export type StafferFirstScreenStats = {
  userId: number;
  name: string;
  screenCount: number;
  medianHours: number;
};

export type DashboardStafferStats = {
  userId: number;
  name: string;
  medianHoursToFirstScreen: number | null;
  screenCount: number;
  laborupSearchSubmits: number;
  internalSourcingSubmits: number;
};

// One Saturday-Friday week of a staffer's stats, oldest week first.
export type DashboardStafferStatsTrendRow = {
  weekStart: string;
  medianHoursToFirstScreen: number | null;
  screenCount: number;
  laborupSearchSubmits: number;
  internalSourcingSubmits: number;
};

// One staffer's period totals for the three dashboard KPIs.
export type DashboardKpiComparisonRow = {
  userId: number;
  name: string;
  medianHoursToFirstScreen: number | null;
  screenCount: number;
  submits: number;
  laborupSearchSubmits: number;
  internalSourcingSubmits: number;
  revenue: number;
};

// One Saturday-Friday week of KPI totals, oldest week first.
export type DashboardKpiTrendRow = {
  weekStart: string;
  medianHoursToFirstScreen: number | null;
  screenCount: number;
  submits: number;
  laborupSearchSubmits: number;
  internalSourcingSubmits: number;
  revenue: number;
};

// One period's numbers: the scope's own totals, and every staffer's.
export type DashboardKpiPeriod = {
  periodStart: string;
  periodEnd: string;
  totals: {
    medianHoursToFirstScreen: number | null;
    screenCount: number;
    submits: number;
    laborupSearchSubmits: number;
    internalSourcingSubmits: number;
    revenue: number;
  };
  comparisons: DashboardKpiComparisonRow[];
};

// Everything the dashboard KPI row needs
export type DashboardKpiOverview = {
  numStaffers: number;
  goals: {
    submits: number;
    revenue: number;
  };
  week: DashboardKpiPeriod;
  month: DashboardKpiPeriod;
  trend: DashboardKpiTrendRow[];
};

export type WeeklySubmits = {
  submits: number;
  submitsGoal: number;
};

export type DashboardStafferScreenBreakdownRow = {
  candidacyId: string;
  candidateName: string;
  jobId: string;
  jobTitle: string;
  companyId: string;
  companyName: string;
  hoursToFirstScreen: number;
};

export type DashboardStafferFunnelStats = {
  userId: number;
  name: string;
  screens: number;
  submits: number;
  interviews: number;
  offers: number;
  placements: number;
};

export type JobPipelineMetrics = {
  submitsCount: number;
  placementsCount: number;
  reqsFilled: number;
  reqsTotal: number;
  medianHoursToFirstScreen: number | null;
  firstScreensByStaffer: StafferFirstScreenStats[];
};

export type GetOtherCandidaciesResponse = {
  id: Candidacy["id"];
  atsJobTitleAndLocation: string | null;
  candidacyAgenticScoreChanges: CandidacyAgenticScoreChange[];
  candidacyOwners: CandidacyOwner[];
  candidacySource: Candidacy["source"];
  createdAt: string;
  company: {
    id: Company["id"];
    name: Company["name"];
  };
  job: {
    id: Job["id"];
    title: Job["title"];
  };
  lastActivityAt: Candidacy["lastActivityAt"];
  lastCandidacyStatusChange: {
    fromStatus: CandidacyStatusChange["fromStatus"];
    statusChangeReason: CandidacyStatusChange["statusChangeReason"];
    toStatus: CandidacyStatusChange["toStatus"];
    byAgent: CandidacyStatusChange["byAgent"];
  };
  screeningAnswers: ScreeningAnswer[] | null;
  textAIOutreachWithProxy: TextAIOutreachWithProxy | null;
  voiceAIOutreachSlim: VoiceAIOutreachSlim | null;
};

export type GetOtherEvaluationsResponse = {
  candidacyId: Candidacy["id"];
  companyName: Company["name"];
  evaluation: CandidacyEvaluationNew;
  evaluationLastUpdatedAt: string | null;
  jobTitle: Job["title"];
};

export type RateAllCandidaciesResponse = {
  message: string;
};

export type GlobalSearchCandidacyResponse = {
  company: {
    id: Company["id"];
    name: Company["name"];
  };
  count: number;
  id: Candidacy["id"];
  job: {
    id: Job["id"];
    title: Job["title"];
  };
  firstName: Candidacy["firstName"];
  lastName: Candidacy["lastName"];
  phone: Candidacy["phone"];
  pendingTalentId: PendingUser["id"];
  status: Candidacy["status"];
  lastStatusChangeByAgent: boolean;
  talentId: User["id"];
};

export type GlobalSearchJobResponse = {
  company: {
    id: Company["id"];
    name: Company["name"];
  };
  id: Job["id"];
  pausedAt: string | null;
  pauseReason: string | null;
  pauseType: JobPauseType | null;
  status: Job["status"];
  title: Job["title"];
};

export type GlobalSearchCompanyResponse = {
  id: Company["id"];
  location: string;
  name: Company["name"];
};

export type GlobalSearchResponse = {
  candidacies: GlobalSearchCandidacyResponse[];
  jobs: GlobalSearchJobResponse[];
  companies: GlobalSearchCompanyResponse[];
};

export type JobFileWithSignedUrlResponse = JobFile & {
  signedUrl: string;
};

// Scraped Job Metrics START
// The guac backend may return null values for any of the metrics so we need to handle that for these

export type JobCountsWithLocationMetricsTableRow = {
  state: string;
  preciseLocationJobCount: number;
  standardLocationJobCount: number;
  total: number;
};

export type JobsMetricsSummary = {
  totalJobs: number | null;
  westTennessee: number | null;
  middleTennessee: number | null;
  eastTennessee: number | null;
};

export type JobsMetricsTimeseriesEntry = {
  date: string;
  totalJobs: number | null;
  middleTennessee: number | null;
  eastTennessee: number | null;
  westTennessee: number | null;
};

export type JobsMetricsTimeseriesResponse = JobsMetricsTimeseriesEntry[];

// Job Location Metrics
export type JobLocationStateCount = {
  total: number;
  precise: number;
  standard: number;
};

type JobLocationMetricsTimeseriesEntry = {
  date: string;
  statesWithCounts: Record<string, JobLocationStateCount>;
};

export type JobLocationMetricsTimeseriesResponse =
  JobLocationMetricsTimeseriesEntry[];

export type StatewiseJobCountPoint = {
  date: string;
  total: number;
  precise: number;
  standard: number;
};

// Compensation Metrics
type CompensationMetricsTimeseriesEntry = {
  date: string;
  statesWithCounts: Record<string, StatewiseCompensationCount>;
};

export type CompensationMetricsTimeseriesResponse =
  CompensationMetricsTimeseriesEntry[];

export type StatewiseCompensationCount = {
  compensationCount: number;
  compensationPercent: number;
  totalCount: number;
};

export type StatewiseCompensationTimeseriesPoint = {
  date: string;
  jobsWithComp: number;
  jobsWithoutComp: number;
  compPercent: number;
};

export type StatewiseCompensationMetricsTableRow = {
  state: string;
  totalJobs: number;
  jobsWithCompensation: number;
  compensationPercent: number;
};
// Scraped Job Metrics END

export type CandidateSheetV2TabCountsResponse = {
  evaluation: number;
  note: number;
  otherCandidacy: number;
  record: number;
};

// Shape emitted by the BE intake-progress util for every progress indicator
// on the job intake page. See
// guac/src/admin-api/admin-job-intake/intake-progress.util.ts.
export type Progress = {
  filled: number;
  total: number;
  percent: number;
};

// Full per-tab + per-item breakdown returned by the job-intake detail endpoint.
export type IntakeCompletion = {
  overall: Progress;
  tabProgress: {
    // Singleton tabs — flat Progress
    generalInfo: Progress;
    talentRecruitingHistory: Progress;
    culture: Progress;
    datesAndUrgency: Progress;
    screeningQuestions: Progress;

    // List tabs — Progress plus per-item breakdown
    stakeholders: {
      progress: Progress;
      /** Keyed by stakeholder.id */
      items: Record<number, Progress>;
    };
    idealCandidateProfiles: {
      progress: Progress;
      /** Keyed by the item's UUID id */
      items: Record<string, Progress>;
    };
    antiIdealCandidateProfiles: {
      progress: Progress;
      items: Record<string, Progress>;
    };
    interviewProcess: {
      progress: Progress;
      laborupRound: Progress;
      /** Keyed by the round's UUID id */
      items: Record<string, Progress>;
    };
  };
};

// Shape returned by every job-intake endpoint (GET, POST, PATCH).
export type JobIntakeWithCompletion = {
  jobIntake: JobIntake | null;
  intakeCompletion: IntakeCompletion | null;
};

export type JobsResponse = {
  job: Job;
  jobInternalConfig: JobInternalConfig | null;
  candidacyOwnerRotation: { rotation: number[] } | null;
  // Users overseeing the job without owning its reqs or candidacies.
  managerUserIds: number[];
  totalCandidates: number;
};

export type JobTrackerResponse = {
  company: Company;
  countsByOwner: Record<number | string, number>;
  countsByRequisitionStatus: Record<RequisitionStatus, number>;
  createdAt: string;
  groupByKey: number | string;
  jobUpdates: JobUpdate[];
  jobId: string;
  jobStatus: JobStatus;
  jobPausedAt: string | null;
  jobPauseReason: string | null;
  jobPauseType: JobPauseType | null;
  jobTitle: string;
  nextDueDate: string | null;
  outstandingRevenue: number;
  unseenExceptionalCandidaciesCount: number;
};

export type JobWithCompanyResponse = {
  id: string;
  title: string;
  company: Company;
};

export type LeaderboardDetailResponse = {
  metric: LeaderboardMetric;
  records: LeaderboardDetailRecord[];
};

export enum PingType {
  CALL = "CALL",
  EMAIL = "EMAIL",
  MESSAGE = "MESSAGE",
}

export type LastPing = {
  at: string;
  direction: CallDirection | EmailDirection | QuoMessageDirection;
  type: PingType;
  status: CallStatus | EmailStatus | QuoMessageStatus;
  stafferId: number;
  sentByAgent?: boolean;
  body: string | null;
};

export type CandidacyJobAndCompanyId = {
  candidacyId: string;
  companyId: string;
  jobId: string;
};

export type MyCandidacyResponse = {
  candidacyId: string;
  firstName: string | null;
  lastName: string | null;
  status: CandidacyStatus;
  lastStatusChangeByAgent: boolean;
  jobId: string;
  jobTitle: string;
  companyId: string;
  companyName: string;
  talentId: number | null;
  lastPing: LastPing | null;
  agenticScores: MinimalScore[];
  owners: { userId: number }[];
  createdAt: string;
};

export type PinnedJobResponse = {
  jobId: string;
  companyAbbreviation: string | null;
  companyId: string;
  companyName: string;
  title: string;
};

export type NotificationResponse = {
  id: number;
  recipientId: number;
  type: NotificationType;
  title: string;
  content: string;
  payload: NotificationPayload;
  // The current priority of the job this was raised for, read live by guac
  jobPriority: JobPriority | null;
  createdAt: string;
  readAt: string | null;
  dismissedAt: string | null;
  // How many times this has been put in front of the staffer
  notifiedCount: number;
};

export type UnreadNotificationCountsResponse = {
  total: number;
  // Only types with at least one unread notification are present
  byType: Partial<Record<NotificationType, number>>;
  excellentCandidateCount: number;
  // When the oldest unread notification of each type was created
  oldestUnreadAtByType: Partial<Record<NotificationType, string>>;
};

export type MarkNotificationsReadByCandidacyResponse = {
  notificationIds: number[];
};

export type UserNotificationPreferenceResponse = {
  id: number;
  type: NotificationType;
  inAppEnabled: boolean;
  emailEnabled: boolean;
  slackEnabled: boolean;
};

export type UserPushPreferenceResponse = {
  type: PushType;
  mode: PushMode;
};

export type ProfileUserUpdateResponse = Profile & {
  permanentStatus: string | null;
};

export type QuoMessageResponse = QuoMessage & {
  user: { firstName: string; lastName: string };
};

export type ResendUserInvitationResponse = {
  success: true;
};

export type SearchResponse = {
  results: SearchResult[];
  nextPageToken: string | null;
};

export type PostSignInResponse = {
  email: string;
  firstName: string;
  id: number;
  lastName: string;
};

export type UpdateProfileUserResponse = Profile & {
  permanentStatus: string | null;
};

export type UpdateUserResponse = {
  id: number;
  notes: string | null;
  professionalSummary: string | null;
};

export interface EnrichmentWorkExperience {
  title: string | null;
  company: string | null;
  location: string | null;
  startMonth: string | null;
  startYear: string | null;
  endMonth: string | null;
  endYear: string | null;
  descriptions: string[];
  currentPosition: boolean;
  startPay: string | null;
  finalPay: string | null;
  compensationType: CompensationType;
}

export interface EnrichmentEducation {
  school: string | null;
  degree: string | null;
  location: string | null;
  startMonth: string | null;
  startYear: string | null;
  endMonth: string | null;
  endYear: string | null;
  current: boolean;
}

export interface EnrichmentCertification {
  name: string | null;
  organization: string | null;
  credentialId: string | null;
  credentialUrl: string | null;
  startMonth: string | null;
  startYear: string | null;
  endMonth: string | null;
  endYear: string | null;
}

// Wire shape returned by POST /admin/companies/:companyId/jobs/:jobId/candidacies/:id/enrichment.
// On follow-up turns, any unchanged section comes back null and is skipped client-side
// when merging into the accumulated EnrichedProfileState.
export interface EnrichmentResponse {
  preamble: string;
  executiveSummary: string | null;
  professionalSummary: string | null;
  workExperiences: EnrichmentWorkExperience[] | null;
  educations: EnrichmentEducation[] | null;
  certifications: EnrichmentCertification[] | null;
  skills: string[] | null;
}

export interface EnrichmentMessage {
  id: string;
  role: EnrichmentMessageRole;
  content: string;
}

export enum EnrichmentMessageRole {
  USER = "user",
  ASSISTANT = "assistant",
}

export interface EnrichmentRequest {
  // Full chat history including the latest user prompt as the trailing message.
  messages: EnrichmentMessage[];
  selectedStafferCallIds: string[];
  selectedStevieCallIds: string[];
  referenceCallTranscript: string | null;
  additionalContext: string | null;
  // The accumulated profile state. Sent on every turn so the model edits
  // ground truth instead of reconstructing it from null-delta outputs.
  currentEnrichedProfile: Omit<EnrichedProfileState, never> | null;
}

// Accumulated enrichment state. First successful turn populates non-null fields;
// follow-up turns override only the fields the model returned non-null. This is what
// gets persisted on "Overwrite profile" / "Add to profile" later.
export interface EnrichedProfileState {
  executiveSummary: string | null;
  professionalSummary: string | null;
  workExperiences: EnrichmentWorkExperience[] | null;
  educations: EnrichmentEducation[] | null;
  certifications: EnrichmentCertification[] | null;
  skills: string[] | null;
}

export type AppliedEnrichmentProfile =
  RouterOutput["internal"]["user"]["active"]["applyEnrichment"];

// Nova ships its .d.ts without strictNullChecks (STF-307), so the trpc input
// types come back with optional fields rather than `string | null`. Nova treats
// an omitted section as "leave untouched" and null/empty as "clear it".
export type ApplyEnrichmentNovaInput =
  RouterInput["internal"]["user"]["active"]["applyEnrichment"]["enrichment"];

export type ApplyEnrichmentArgs = {
  candidacyId: string;
  enrichedProfile: EnrichedProfileState;
  userId: number;
};

export type ApplyEnrichmentResult = {
  // Null when the enrichment produced no executive summary, so no candidacy PATCH ran.
  candidacy: Candidacy | null;
  profile: AppliedEnrichmentProfile;
};

// Bundles every piece of enrichment-input state owned at the candidate-sheet
// level so consumers can pass a single object instead of N props each.
export interface EnrichmentInputsState {
  selectedStafferCallIds: Set<string>;
  setSelectedStafferCallIds: Dispatch<SetStateAction<Set<string>>>;
  selectedStevieCallIds: Set<string>;
  setSelectedStevieCallIds: Dispatch<SetStateAction<Set<string>>>;
  referenceCallTranscript: string | null;
  setReferenceCallTranscript: Dispatch<SetStateAction<string | null>>;
  additionalContext: string | null;
  setAdditionalContext: Dispatch<SetStateAction<string | null>>;
  prompt: string;
  setPrompt: Dispatch<SetStateAction<string>>;
  messages: EnrichmentMessage[];
  setMessages: Dispatch<SetStateAction<EnrichmentMessage[]>>;
  enrichedProfile: EnrichedProfileState;
  setEnrichedProfile: Dispatch<SetStateAction<EnrichedProfileState>>;
  isEnriching: boolean;
  setIsEnriching: Dispatch<SetStateAction<boolean>>;
}

/* Sadie simulator (guac: /api/admin/relationships/simulator) */

export type SadieSimulatorTester = {
  talentId: number;
  // Null for a tester who has never texted Sadie.
  firstName: string | null;
  isSimulating: boolean;
};

export type SadieSimulatorTesters = {
  testers: SadieSimulatorTester[];
  models: string[];
};

// One edit to Sadie's prompt. With `after` it adds a section after that one,
// with empty text it removes the section, and otherwise it replaces its text.
export type SadieSimulatorPromptChange = {
  heading: string;
  text: string;
  after?: string;
};

export type SadieSimulatorPromptSection = {
  heading: string;
  text: string;
};

export type SadieSimulatorPromptMiss = {
  heading: string;
  reason: "NOT_FOUND" | "ALREADY_THERE" | "AFTER_NOT_FOUND";
};

// How the prompt changes left one section, for the simulator's prompt view.
// A removed section keeps its place so the removal shows.
export type SadieSimulatorPromptDiffSection = {
  heading: string;
  text: string;
  status: "SAME" | "CHANGED" | "ADDED" | "REMOVED";
  // The section's text as built. Null for an added one.
  before: string | null;
};

// One line of a changed section, against the same section as built.
export type SadieSimulatorLineDiff = {
  kind: "SAME" | "ADDED" | "REMOVED";
  text: string;
};

// The prompt Sadie's last turn built, before any prompt changes, and the
// changes that did nothing on it.
export type SadieSimulatorPrompt = {
  sections: SadieSimulatorPromptSection[];
  misses: SadieSimulatorPromptMiss[];
  builtAt: string;
};

// A tester the simulator page is driving. A null model runs the usual one.
export type SadieSimulatorState = {
  model: string | null;
  startedAt: string;
  promptChanges?: SadieSimulatorPromptChange[];
};

export type SadieSimulatorMedia = {
  url: string;
  type: string;
  description?: string;
};

export type SadieSimulatorMessage = {
  // The row's quoId.
  id: string;
  from: "WORKER" | "SADIE";
  body: string;
  sentAt: string;
  // A worker's tapback, which is stored as its own row.
  isTapback: boolean;
  media: SadieSimulatorMedia[];
  // Sadie's tapback on this worker text.
  tapback: string | null;
  // The screen effect Sadie sent this text with, like confetti.
  effect: string | null;
};

export type SadieSimulatorSession = {
  id: string;
  kind: string;
  status: string;
  openedAt: string;
};

export type SadieSimulatorThread = {
  // Null when the tester is texting from their phone.
  simulator: SadieSimulatorState | null;
  messages: SadieSimulatorMessage[];
  // Null before Sadie's first reply.
  prompt: SadieSimulatorPrompt | null;
  sessions: SadieSimulatorSession[];
  dotsUp: boolean;
};

// Admin monitoring for the Staffer assistant (guac `admin/assistant`).
export type AssistantPerson = { id: number; name: string };

export type AssistantThreadRef = {
  threadId: string;
  user: AssistantPerson;
  staffer: AssistantPerson;
  // A Staffer's own thread, as opposed to an admin's view-as thread.
  isOwn: boolean;
  lastMessageAt: string | null;
};

export type AssistantRunStats = {
  threads?: number;
  conversations?: number;
  pastPrefilter?: number;
  classified?: number;
  flagged?: number;
  perStaffer?: {
    stafferUserId: number;
    signals: number;
    planned: number;
    sent: number;
    held: number;
    // Planned but held until the Staffer's conversation ends.
    deferred?: number;
    // Trigger 2: quiet customers due for this Staffer, and sent in the
    // roundup.
    quietDue?: number;
    quietSent?: number;
    // Trigger 3b: candidate news due for this Staffer, and sent.
    newsDue?: number;
    newsSent?: number;
  }[];
  claimed?: number;
};

export type AssistantRunLog = {
  id: string;
  // 'email_scan' | 'schedule_tick'
  kind: string;
  startedAt: string;
  finishedAt: string | null;
  durationMs: number | null;
  stats: AssistantRunStats | null;
  error: string | null;
};

export type AssistantOverviewThread = AssistantThreadRef & {
  chatTurns: number;
  voiceTurns: number;
  assistantReplies: number;
  failedReplies: number;
  proactive: {
    nudge: number;
    renudge: number;
    digest: number;
    reminder: number;
    heldForChat: number;
    followUp: number;
    // Daily roundups of customers the Staffer is waiting on.
    quietRoundup: number;
    plan: number;
    postInterview: number;
    candidateNews: number;
    // daily_briefing pushes, by delivery.
    briefing: number;
    wrapup: number;
  };
  items: {
    total: number;
    open: number;
    resolvedByReply: number;
    resolvedByEvidence: number;
    dismissed: number;
    medianResolveMinutes: number | null;
  };
  activeSchedules: number;
};

export type AssistantOverview = {
  since: string;
  days: number;
  threads: AssistantOverviewThread[];
  lastRuns: {
    emailScan: AssistantRunLog | null;
    scheduleTick: AssistantRunLog | null;
  };
};

export type AssistantProactiveAsk = {
  ask: string;
  status: "open" | "handled" | "unclear" | "scheduled";
  candidate: string | null;
  resolution: { kind: string; at: string | null; detail: string } | null;
};

export type AssistantProactiveItem = {
  key: string;
  // unanswered_email = the customer is waiting on Laborup; quiet_customer =
  // Laborup is waiting on the customer.
  kind: "unanswered_email" | "quiet_customer";
  subject: string;
  customer: { name: string; email: string; company: string | null };
  // The customer's email, or (quiet_customer) when Laborup started waiting.
  sentAt: string;
  followUpsSent?: number;
  gmailThreadId: string | null;
  urgency: string;
  asks: AssistantProactiveAsk[];
  reason: string | null;
  status: "open" | "resolved" | "dismissed";
  resolvedAt: string | null;
  resolvedBy: "reply" | "evidence" | "staffer" | null;
  // The Staffer snoozed it until then (ISO).
  snoozedUntil?: string | null;
};

// A card under a chat reply, as stored (ids only).
export type AssistantAttachmentRef =
  | { kind: "candidate"; candidacyId: string }
  | { kind: "job"; jobId: string }
  | { kind: "queue"; title: string; candidacyIds: string[] }
  | { kind: "interview"; interviewId: number }
  | { kind: "reminder"; scheduleId: string };

export type AssistantToolCall = {
  name: string;
  durationMs: number;
  // The call's arguments (long strings cut), e.g. query_db's SQL.
  args?: Record<string, unknown>;
  outputChars?: number;
  // The start of what the tool returned.
  outputPreview?: string;
};

export type AssistantMessageMetadata = {
  // 'reply' or 'voice' for chat; anything else is a push Phoebe sent on her
  // own (unanswered_email, quiet_customer, post_interview, candidate_news,
  // daily_briefing, reminder, plan, …).
  trigger?: string;
  replyToMessageId?: string;
  quote?: { messageId: string; role: "USER" | "ASSISTANT"; excerpt: string };
  signalKeys?: string[];
  scheduleId?: string;
  delivery?: AssistantMessageDelivery;
  // unanswered_email: sent on its own because a reply is needed within hours.
  important?: boolean;
  // What sent a proactive email message.
  origin?: AssistantDeliveryOrigin;
  // follow_up: why it was scheduled.
  why?: string;
  // held_for_chat: when the scan would have sent it.
  plannedAt?: string;
  attachments?: AssistantAttachmentRef[];
  suggestedReplies?: string[];
  toolCalls?: AssistantToolCall[];
  error?: string;
  items?: AssistantProactiveItem[];
};

export type AssistantDeliveryOrigin = "scan" | "follow_up" | "held_for_chat";

export type AssistantMessageDelivery =
  | "nudge"
  | "renudge"
  | "digest"
  | "briefing"
  | "wrapup";

export type AssistantItemDelivery = {
  kind: AssistantMessageDelivery;
  at: string;
  origin: AssistantDeliveryOrigin;
  plannedAt: string | null;
  why: string | null;
};

export type AssistantScheduleDeferral = {
  at: string;
  until: string;
  reason: "in_conversation" | "quiet_hours" | "plan_not_ready";
};

export type AssistantMessage = {
  id: string;
  role: "USER" | "ASSISTANT";
  source: "TEXT" | "VOICE";
  status: "PENDING" | "COMPLETE" | "FAILED";
  content: string;
  createdAt: string;
  liveSessionId: string | null;
  metadata: AssistantMessageMetadata | null;
};

export type AssistantThreadMessages = {
  thread: AssistantThreadRef;
  messages: AssistantMessage[];
  hasMore: boolean;
};

export type AssistantItemRow = AssistantProactiveItem & {
  threadId: string;
  messageId: string;
  staffer: AssistantPerson | null;
  firstSurfacedAt: string;
  lastSentAt: string;
  deliveries: AssistantItemDelivery[];
};

export type AssistantScheduleRow = {
  id: string;
  thread: AssistantThreadRef | null;
  source: "SYSTEM" | "STAFFER" | "AGENT";
  status: "ACTIVE" | "PAUSED" | "DONE" | "CANCELED";
  action: string;
  payload: {
    text?: string;
    why?: string;
    signalKey?: string;
    mailboxUserId?: number;
    gmailThreadId?: string;
    // deliver_held: nudges held while the Staffer was chatting.
    held?: { signalKey: string; delivery: "nudge" | "renudge" | "digest" }[];
    // deliver_held: when the scan would have sent it.
    plannedAt?: string;
    // Each time it was pushed later instead of running.
    deferrals?: AssistantScheduleDeferral[];
  };
  recurrence: {
    frequency: "daily" | "weekly";
    time: string;
    weekdays?: number[];
  } | null;
  timezone: string;
  nextRunAt: string | null;
  lastRunAt: string | null;
  runCount: number;
  claimedAt: string | null;
  createdAt: string;
};

// The Sadie dashboard for one window, as guac computes it. Grows a metric at
// a time. Rates are 0 to 1 with their counts.
export interface SadieDashboardResponse {
  range: SadieDashboardRange;
  warmRelationships: SadieDashboardStat;
  warmShareOfTexted: SadieDashboardStat;
  // First texted in the window, including any who opted out later.
  workersFirstTexted: SadieDashboardStat;
  // Out of everyone Sadie could reach at some point in the window.
  optOutRate: SadieDashboardStat;
  textMessages: SadieDashboardTextMessages;
  // Laborup jobs Sadie applied workers to after a yes to her pitch.
  internalApplications: SadieDashboardStat;
  externalApplications: SadieDashboardExternalApplications;
  referrals: SadieDashboardReferrals;
}
